/**
 * Design tokens — single source of truth for every color literal in the app.
 *
 * Rules of engagement:
 *  - If a color expresses brand identity (buttons, links, accents), reach for
 *    `theme.palette.primary.*` via MUI's `useTheme()` / `sx` callbacks. Do NOT
 *    hardcode purple-ish hexes.
 *  - If a color is a neutral (white, gray, black-ish ink), import from
 *    `NEUTRAL` below.
 *  - If a color expresses status (error/warning/success/info), use
 *    `theme.palette.{error,warning,success,info}.*`.
 *  - If a color is functional/categorical (role chip, department type), it
 *    lives outside the theme system — see `CATEGORICAL` below and `utils/categoricalColors`.
 *
 * Adding a one-off literal to a feature file is a code-review failure.
 */

// `alpha` is the only import this module takes: a token that is a *derivation* of another token (an
// opacity ramp off a band color) belongs here rather than at 40 call sites, and MUI's helper is the same
// one every consumer would otherwise call inline.
import { alpha } from "@mui/material/styles";

/** Pure white. Use for surface/paper where the app already expects white. */
export const SURFACE = "#FFFFFF";
/** Pure black — rarely correct. Prefer INK.PRIMARY for body text. */
export const INK_BLACK = "#000000";

/**
 * Neutral gray scale (Material-ish). Use for borders, dividers, disabled
 * states, and surfaces that should NOT shift with the user's theme color.
 */
export const NEUTRAL = {
	50: "#F8F9FC",
	100: "#F5F5F5",
	150: "#F1F3F9",
	200: "#E1E6EF",
	300: "#E0E0E0",
	350: "#D9DBDE",
	400: "#D1D5DB",
	450: "#B7BCC5",
	500: "#9CA3AF",
	600: "#525252",
	700: "#374151",
	800: "#262626",
	900: "#171717",
} as const;

/**
 * Body text scale. PRIMARY for default body text; SECONDARY for muted/labels;
 * DISABLED for unavailable controls.
 */
export const INK = {
	PRIMARY: NEUTRAL[900],
	SECONDARY: NEUTRAL[700],
	MUTED: NEUTRAL[600],
	DISABLED: NEUTRAL[400],
	ON_DARK: SURFACE,
} as const;

/** Status colors. Mirrored into theme.palette so MUI components pick them up. */
export const STATUS = {
	ERROR: {
		main: "#D32F2F",
		light: "#FFEBEE",
		dark: "#C62828",
		contrastText: SURFACE,
	},
	WARNING: {
		main: "#ED6C02",
		light: "#FFF4E5",
		dark: "#663C00",
		contrastText: SURFACE,
	},
	SUCCESS: {
		main: "#2E7D32",
		light: "#E8F5E9",
		dark: "#1B5E20",
		contrastText: SURFACE,
	},
	INFO: {
		main: "#0288D1",
		light: "#E1F5FE",
		dark: "#01579B",
		contrastText: SURFACE,
	},
} as const;

/**
 * Ordered target-distance ramp for dense comparison matrices. Unlike `STATUS`, these are several
 * steps of one escalating signal: a green near-target band, then yellow through orange and
 * increasingly deep red. The scale is intentionally stable across tenant themes so the severity
 * meaning never moves.
 */
export const HEATMAP_SEVERITY = {
	SUCCESS: { bgcolor: STATUS.SUCCESS.light, color: STATUS.SUCCESS.dark },
	WATCH: { bgcolor: "#FFF9C4", color: "#5F4B00" },
	CAUTION: { bgcolor: "#FFF0A8", color: "#6A4B00" },
	ELEVATED: { bgcolor: "#FFE0A3", color: "#734000" },
	HIGH: { bgcolor: "#FFCC80", color: "#783600" },
	ALERT: { bgcolor: "#FFB074", color: "#7A2E00" },
	DANGER: { bgcolor: "#FFAB91", color: "#7A2600" },
	CRITICAL: { bgcolor: "#EF5350", color: "#FFFFFF" },
	SEVERE: { bgcolor: STATUS.ERROR.dark, color: STATUS.ERROR.contrastText },
} as const;

/**
 * Violet tone for the "Reschedule" candidate status chip. Fixed (not tenant-themed), like the STATUS
 * tones above — `main` tints the light lavender fill/border, `dark` is the readable purple text/icon.
 * A clear periwinkle (not the navy brand-dark) so it reads distinctly from primary (PENDING) and INFO blue.
 */
export const STATUS_RESCHEDULE = {
	main: "#8B7FD6",
	dark: "#5A4AA8",
} as const;

/**
 * Coral / salmon used for HPPD-violation chips and other "out-of-bounds"
 * indicators. Distinct from ERROR (which is reserved for actionable failures).
 */
export const ALERT_CORAL = "#FE6C5F";

/**
 * Legacy MUI secondary accent. The user-driven theme governs the *primary*
 * color only; this gold is the fixed secondary used by MUI components that
 * read `palette.secondary.main` and is not exposed to feature code.
 */
export const SECONDARY_GOLD = "#FFD700";

/**
 * Functional indicators that are NOT brand-driven — they encode information
 * (day vs night shift, charge nurse, etc.) and must stay stable across themes.
 */
export const SHIFT_DAY = "#FFBF00";
export const SHIFT_NIGHT_FALLBACK = "#5F5CE8";
/** Amber row-highlight used to mark a focused nurse in gap views. */
export const NURSE_ROW_HIGHLIGHT = "#FFECB1";

/**
 * Shift-time WINDOW ramp — the visual language for "which of the department's shift windows", layered
 * on the binary day/night indicators above rather than replacing them.
 *
 * A department runs more than two windows (6a–2p, 2p–10p, 10p–6a, …), so each window is BANDED
 * day-or-night through `getShiftTypeFromStartTime` and then separated WITHIN its band by an alpha ramp
 * ordered by start time (earliest = lightest). Every step is derived from `SHIFT_DAY` /
 * `SHIFT_NIGHT_FALLBACK` so the window ramp can never drift away from the day/night language its
 * consumers (`DayNightSelector`, `ShiftTypeFilterChips`, `TimeTitleRow`) already speak.
 *
 * `*_INK` is FIXED rather than derived from the tint — the same reasoning as `SHIFT_PILL.WORKED_INK`:
 * `SHIFT_DAY` at full strength cannot carry white text, and an ink derived per-step would drift out of
 * contrast on the deepest one. Both inks clear WCAG AA (4.5:1) on the LIGHTEST *and* the DEEPEST step of
 * their own band (day ink on the 0.38 amber tint measures ≈5.9:1, night ink on the 0.38 indigo tint
 * ≈6.1:1), so a tint is safe as a background under 11–13px text.
 *
 * The ramp is 4 steps because the Monthly view caps its window set at 4 (`MONTHLY_MAX_SHIFT_WINDOWS`)
 * and buckets the rest into `Other`. If a consumer ever exceeds it, cycle the ramp: the window LABEL and
 * the day/night GLYPH still tell the rows apart, so color is never the only carrier of meaning.
 */
export const SHIFT_WINDOW = {
	DAY_TINTS: [alpha(SHIFT_DAY, 0.14), alpha(SHIFT_DAY, 0.22), alpha(SHIFT_DAY, 0.3), alpha(SHIFT_DAY, 0.38)],
	NIGHT_TINTS: [alpha(SHIFT_NIGHT_FALLBACK, 0.14), alpha(SHIFT_NIGHT_FALLBACK, 0.22), alpha(SHIFT_NIGHT_FALLBACK, 0.3), alpha(SHIFT_NIGHT_FALLBACK, 0.38)],
	/** Left band rail — a 3px rule the eye catches peripherally, so it carries near-full chroma. */
	DAY_RAIL: alpha(SHIFT_DAY, 0.9),
	NIGHT_RAIL: alpha(SHIFT_NIGHT_FALLBACK, 0.9),
	DAY_INK: "#7A4E12",
	NIGHT_INK: "#312E9E",
	/** The `Other` overflow bucket belongs to no band, so it reads neutral — never a third invented hue. */
	OTHER_TINT: NEUTRAL[150],
	OTHER_RAIL: NEUTRAL[450],
	OTHER_INK: INK.SECONDARY,
} as const;

/**
 * Severity tiers (recommendations, high-risk indicators). Distinct from STATUS
 * because they grade the same kind of event (low → high) rather than naming an
 * event type. Stable across themes.
 */
export const SEVERITY = {
	HIGH: "#EE5F4B",
	MEDIUM: "#EEA54B",
} as const;

/**
 * Categorical badge colors for the staff table (overtime, agency, missing
 * punches, etc.). These indicate status; they're not brand-tied so they stay
 * stable across user-chosen themes.
 */
export const STATUS_BADGE = {
	CORAL: "#FE7B6F",
	PEACH: "#FFE5B4",
	AMBER_INK: "#B45309",
	ORANGE: "#FF9500",
	MUTED_INK_80: "rgba(77, 85, 121, 0.8)",
	GRAY_INK_80: "rgba(107, 111, 143, 0.8)",
	LILAC: "#E8DEF8",
	LILAC_INK: "#5B3A8A",
	WARN_BG: "#FFF4E5",
	WARN_INK: "#FF8C00",
	ERROR_BG: "#FEE2E2",
	ERROR_INK: "#DC2626",
	INFO: "#4A90E2",
	SUCCESS_BG: "#D1FAE5",
	SUCCESS_INK: "#065F46",
	/**
	 * Resolves to the user's chosen primary at runtime via the CSS variable
	 * `--inhouse-primary`. Use this anywhere you previously wanted brand
	 * purple. Do NOT pass it through `alpha()`/`darken()`/`lighten()` —
	 * those reject CSS vars; use `BRAND_FALLBACK_HEX` for that case.
	 */
	BRAND_FALLBACK: "var(--inhouse-primary)",
	/**
	 * Static brand-purple hex. Use ONLY for `alpha()`/`darken()`/`lighten()`
	 * call sites where MUI rejects CSS vars. Result will not retint when the
	 * user picks a new theme color.
	 */
	BRAND_FALLBACK_HEX: "#857EB6",
} as const;

/**
 * Disciplinary violation identity colors — the chip on a violation row and the icon on its summary
 * card. Categorical: each pair names *which* violation this is, so like STATUS_BADGE above they stay
 * fixed across user-chosen themes (a re-tinted Late Arrival would stop reading as Late Arrival).
 * `BG` is the chip wash, `INK` the text/icon on it. Grouped by tier, not by type — the types that
 * share a tier are meant to share a color, and the two absence/timing tiers reuse the shared badge
 * pairs above rather than re-typing those hexes.
 */
export const DISCIPLINARY_VIOLATION = {
	/** Didn't work the shift at all — NCNS, Call In. */
	ABSENCE_BG: STATUS_BADGE.ERROR_BG,
	ABSENCE_INK: STATUS_BADGE.ERROR_INK,
	/** Worked it, off the scheduled window — Late Arrival, Home Early. */
	TIMING_BG: STATUS_BADGE.WARN_BG,
	TIMING_INK: STATUS_BADGE.WARN_INK,
	/** Shift Delta. Wash only — its ink is `primary.dark`, the one violation tinted by the theme. */
	DELTA_BG: "#EDE7F6",
	/** Skipped Lunch. */
	LUNCH_BG: "#E0F2F1",
	LUNCH_INK: "#00796B",
	/** Missing Punch In. */
	PUNCH_IN_BG: "#E0F2FE",
	PUNCH_IN_INK: "#0369A1",
	/** Missing Punch Out — indigo, so the two halves of a punch pair read as distinct events. */
	PUNCH_OUT_BG: "#E0E7FF",
	PUNCH_OUT_INK: "#4338CA",
	/** Missing Punch summary-card icon — brighter than PUNCH_IN_INK, which has to carry text on a wash. */
	PUNCH_ICON: "#0EA5E9",
} as const;

/**
 * Coverage / staffing-deficit diverging scale. Encodes a signed
 * surplus/deficit cell against two thresholds (safety floor + target):
 *  - CRITICAL: below the minimum (safety floor) — red. The heaviest tier, but a
 *    low-chroma wash: in a dense grid the sheer NUMBER of tinted cells is what
 *    creates weight, so intensity per cell stays restrained and the ink + bar
 *    carry the severity instead.
 *  - WARNING: at/above the floor but under target — amber. Deliberately calmer
 *    than CRITICAL (lighter wash, softer ink) so it stays a secondary signal
 *    and lets red dominate.
 *  - MET: exactly at target (`0`) — near-muted. The "no problem here" state
 *    should carry almost no weight; only problems and surplus draw the eye.
 *    NOT green (green reads as "good news" and competes with the red gaps).
 *  - SURPLUS: over target on a REQUIRED slot (`+N`) — a faint distinct positive
 *    tint so over-staffing is visible and reads differently from exactly-met.
 *  - MUTED: not required that day (off day / inactive week) — never a gap
 * Each tier carries a soft `BG` (cell fill) and a saturated `INK` (the number),
 * tuned for a dense scan. Stable across themes — coverage legibility
 * must not shift when the user picks a new accent.
 */
export const COVERAGE = {
	// These two deficit fills are the MASTER-SCHEDULE coverage scale
	// (`UIToolkit/Coverage/CoverageDeltaCell`, where WARNING_BG also carries the two
	// SURPLUS tiers, plus one header state in `CoverageSectionHeader`). They are not
	// a general "short / critically short" palette: another feature that wants a
	// warmer or louder gap fill declares its own (see `GAP_TONE`) rather than moving
	// these, because moving these restyles the coverage grids nobody asked to change.
	//
	// Softened from #FCE5E3: same hue, lower chroma. A grid-wide wash of many tinted
	// cells read far stronger in aggregate than any single swatch does in isolation.
	// Lightening the fill also RAISES the ink contrast, so AA is preserved by construction.
	CRITICAL_BG: "#FDEFEE",
	CRITICAL_INK: "#B3261E",
	// Softened from #FDF4E6 alongside CRITICAL_BG, keeping the two tiers' relative
	// weight (amber stays the calmer of the two).
	WARNING_BG: "#FEF9F1",
	// Darkened from #A9742B (3.69:1, failed AA) to clear WCAG AA-normal 4.5:1 on
	// WARNING_BG — measured 5.36:1 — while staying clearly amber, not red. This is
	// deliberately NOT a true yellow: a saturated yellow ink on a pale fill lands
	// near 1.8:1, and these run as small as 11px, so there is no large-text exemption
	// to spend. Saturated yellow belongs on the dot (WARNING_MARKER), no text on it.
	WARNING_INK: "#8C5E1F",
	MET_BG: "#FAFBFC",
	MET_INK: "#8A93A3",
	SURPLUS_BG: "#E9F5EF",
	SURPLUS_INK: "#2E7D5B",
	MUTED_BG: "#FBFBFC",
	MUTED_INK: "#C4CAD4",
	/** PTO-driven dip marker — corner-fold glyph fill. Shares WARNING ink hue. */
	PTO_MARK: "#8C5E1F",
	/** Leading severity bar — a 3px left-edge rule, so a column of gaps reads as one continuous edge.
	    Tinted, not saturated: at full chroma a deficit-heavy grid read as alarming, not scannable. */
	CRITICAL_BAR: "#E09A93",
	WARNING_BAR: "#E9CE9B",
	/** Saturated tier MARKER — carries no text, so it can be the colour the tier is actually CALLED.
	    Reusing the INK here is what made the "yellow" tier's marker read brown. */
	CRITICAL_MARKER: "#D2392E",
	WARNING_MARKER: "#F5C400",
	/**
	 * Subtle inner hairline on a populated cell on hover — a tokenized ring that
	 * lifts the focused cell off the lattice without shifting layout (inset, no
	 * border-box change). Neutral so it never competes with the tier color.
	 */
	CELL_HOVER_RING: "#B6BECB",
	/**
	 * Surface + ink for a coverage cell rendered in COUNT emphasis (`CoverageCellEmphasis`) — a headcount
	 * that happens to know its target, rather than a grade that happens to be a number.
	 *
	 * Deliberately tier-INDEPENDENT: one surface and one ink for EVERY tier, and no severity mark of any
	 * kind beside them — the number is the whole cell. Derived from the existing neutral scale rather than
	 * a new hue: this is the app's quietest card surface (`NEUTRAL[50]`) under its standard body ink,
	 * which is exactly the "this is just data" register. The target is not drawn here at all; it stays in
	 * the `"ratio"` label and the `ariaLabel`, which always name both counts.
	 */
	COUNT_BG: NEUTRAL[50],
	COUNT_INK: INK.PRIMARY,
} as const;

/**
 * Z-index ladder for the sticky Master Schedule v3 grid layers. The grids freeze
 * three leading columns (left) and two header rows (top); their stacking is a
 * fixed ladder that used to be re-typed as raw integers (2–6) in every grid file.
 * Naming it means a future sticky layer slots in without re-auditing bare numbers.
 * Ordered low→high: a frozen body cell sits under the header rows, which sit under
 * the top-left corner where the frozen row meets the frozen column.
 */
export const GRID_Z = {
	/** A body leading (identity) cell, frozen left. */
	STICKY_CELL: 2,
	/** The day-of-week header row, frozen top. */
	DAY_HEADER: 3,
	/** A leading-column header label, frozen left. */
	LEADING_HEADER: 4,
	/** The week header row, frozen top. */
	WEEK_HEADER: 5,
	/** The top-left corner block — frozen on both axes, above everything. */
	CORNER: 6,
} as const;

/**
 * Dense-grid font ramp (px) for the Master Schedule v3 grids. These sit below the
 * app's regular typography scale — the grid needs a finer set of small sizes than
 * `theme.typography` or `FONT_SIZE` offer. Consolidating them here also removes the
 * accidental 11-vs-12 / 9-vs-9.5 drift that had crept in across the three grids.
 */
export const GRID_FONT = {
	/** 9.5px — smallest metadata (rule chip, active/upcoming pill). */
	MICRO: 9.5,
	/** 10.5px — sub-metadata. */
	XS: 10.5,
	/** 11.5px — day-of-week header, role code. */
	SM: 11.5,
	/** 12.5px — leading-cell body text, week label. */
	BASE: 12.5,
	/** 13px — emphasized cell body. */
	MD: 13,
	/** Letter-spacing steps used across the grid chrome. */
	LETTER_SPACING: {
		WIDE: "0.06em",
		HEADER: "0.04em",
		TIGHT: "0.01em",
	},
} as const;

/**
 * Week / weekend / hover tints for the schedule grid, expressed as the single
 * canonical lighten/alpha amount applied to `theme.palette.primary.*` at the call
 * site (the value depends on the user's chosen accent, so it can't be a static
 * hex). This replaces ~10 hand-tuned percentages that had drifted between the
 * three grids — there is now one answer to "how light is an active week".
 */
export const GRID_TINT = {
	/** lighten(primary.light, x) — the current (active) week header. */
	ACTIVE_WEEK: 0.58,
	/** lighten(primary.light, x) — a future (upcoming) week header. */
	UPCOMING_WEEK: 0.72,
	/** lighten(primary.main, x) — a weekend day-of-week header (one notch softer). */
	WEEKEND: 0.18,
	/** lighten(primary.light, x) — the week-header hover wash. */
	HOVER_WASH: 0.48,
	/** lighten(primary.main, x) — the frozen leading-column header background. */
	LEADING_HEADER_BG: 0.95,
	/** alpha(primary.main, x) — the "upcoming" badge background. */
	ACTIVE_BADGE_ALPHA: 0.12,
} as const;

/**
 * Corner-radius scale (× `theme.shape.borderRadius`) for the grid's inline chips
 * and pills, so the next contributor picks a name instead of a bare number.
 */
export const GRID_RADIUS = {
	/** A small status badge (OT, charge). */
	BADGE: 0.5,
	/** A metadata chip (rule chip, active/upcoming pill). */
	CHIP: 0.75,
	/** A panel / the grid container. */
	PANEL: 2,
} as const;

/** Geometry for the compact support-only HRIS integration summary grids. */
export const INTEGRATION_SUMMARY = {
	GRID_HEIGHT: 280,
} as const;

/**
 * Worked-day markers for the People-axis rotation grid (and any future "who works which day"
 * projection). Shift type is NOT encoded on this axis: a nurse's `shiftType` is a single per-nurse
 * value, so tinting by it painted every cell of a mixed rotation the same wrong hue. A worked cell
 * is filled with its JOB's (or role's) hue from the app-wide categorical palette instead — see
 * `useJobColors` / `roleGroupColor` — which is why there is no worked-fill token here.
 * `WORKED_INK` is the fixed dark ink those fills carry (fixed, not derived, so contrast holds for
 * every hue the palette can produce); `OFF` is the muted not-working day; `CYCLE_ON` / `CYCLE_OFF`
 * drive the normalized cycle-strip dots (●/○) and the controls that flip a row into it. Stable
 * across themes — rotation legibility must not shift when the user picks a new accent.
 */
export const SHIFT_PILL = {
	/** Ink for a worked day's glyph, PTO outline, and corner fold. */
	WORKED_INK: INK.SECONDARY,
	/** A normally-not-worked day in the projection — recedes. */
	OFF_BG: "#FBFBFC",
	OFF_INK: "#C4CAD4",
	/** Filled (●) / empty (○) dot for the normalized cycle strip. */
	CYCLE_ON: "#5F5CE8",
	CYCLE_OFF: "#D1D5DB",
} as const;

/**
 * Geometry (px) of the People-axis day marker — the worked pill and the Detail lines stacked under
 * it. Sub-4px values, so they are deliberately NOT MUI `theme.spacing` steps (the 8px scale cannot
 * express them without changing the rendered layout); they live here rather than inline because the
 * pill and its Detail lines have to keep the same optical inset, and the pill is sized against
 * `gridGeometry`'s `DAY_COL_MIN` — a value one component must not be free to outgrow on its own.
 */
export const PEOPLE_MARKER = {
	/** Pill width FLOOR, so short labels all keep one pill width. A long label grows the pill instead,
	    which is why the marker also caps it at the column width — `getJobInitials` can return more
	    characters than `DAY_COL_MIN` holds. */
	PILL_MIN_W: 28,
	PILL_H: 16,
	PILL_PAD_X: "3px",
	/** Horizontal inset of a Detail line, matching the pill's so the stack reads as one column. */
	DETAIL_PAD_X: "3px",
	/** Breathing room the 32px summary row doesn't allocate, added only when Detail lines render. */
	DETAIL_GAP: "2px",
	DETAIL_PAD_Y: "3px",
	/** Day-off glyph in a PTO cell — matches the profile week grid's treatment, scaled to this row. */
	PTO_ICON: 16,
	PTO_GAP: "1px",
} as const;

/**
 * Overtime badge for the People-axis "OT by recurring" (Detail) view: a week /
 * cycle whose rotation hours push a nurse past the OT threshold. Coral rather
 * than the actionable ERROR red — OT is a planning signal to notice, not a
 * failure. Soft `BG` for the badge fill, saturated `INK` for its text.
 */
export const OVERTIME = {
	BG: "#FFE9E5",
	INK: "#C0432F",
} as const;

/**
 * Management Center Hours Summary series palette — one color per hours category, shared by the
 * table rows, the expanded per-nurse breakdown, and the details panel so a category reads
 * identically wherever it appears.
 *
 * Categorical, and deliberately stable across themes: these encode WHICH kind of hours a row is,
 * so the mapping must not shift with the accent color any more than the role palette does.
 *
 * Consolidated here from feature-local hex literals in `HoursSummary/utils.ts`. Values are
 * unchanged — a relocation, not a restyle. `CONSECUTIVE_OT` (DAT-289) is teal to stay legible
 * between the warm Weekly/Daily OT pair it sits between in the table.
 */
export const HOURS_SUMMARY = {
	NEEDED: "#4CAF50",
	WEEKLY_OT: "#F57C00",
	CONSECUTIVE_OT: "#00897B",
	DAILY_OT: "#E53935",
	AGENCY: "#1565C0",
	OVERRUN: "#7B1FA2",
	EXTRA_PAY: "#2E7D32",
} as const;

/**
 * Management Center labor-mix palette — the ONE mapping from a labor-mix concept to a color, shared
 * by every widget on the page (the PPD donut, Cost Deep Dive, Staff Mix) so a concept reads the same
 * wherever it appears (APP-766).
 *
 * The semantics, which are the whole point:
 *
 * | concept          | color                | reads as                          |
 * | ---------------- | -------------------- | --------------------------------- |
 * | `BASE`           | soft sage green      | the in-house baseline             |
 * | `DAILY_OT`       | primary 300          | ─┐                                |
 * | `WEEKLY_OT`      | primary 500          |  ├ escalating premium cost        |
 * | `CONSECUTIVE_OT` | primary 700          | ─┘                                |
 * | `AGENCY`         | fixed steel blue     | labor bought from OUTSIDE         |
 * | `TOTAL`          | dark neutral ink     | the sum, not a band               |
 *
 * **The OT ramp encodes ORDER, not identity.** Daily → Weekly → Consecutive is a monotonic scale of
 * premium cost, and the reader's job is to see which way the stack is leaning, not to name a hue.
 * That is exactly why the ramp MAY be accent-derived where `HOURS_SUMMARY` above may not: a
 * re-tinted `HOURS_SUMMARY.AGENCY` stops reading as Agency (it is categorical — one hue per
 * category, arbitrary and memorized), whereas a re-tinted ramp is still a ramp. The two blocks
 * differ deliberately; do not "unify" them.
 *
 * Corollary: the OT stops are defined by shade POSITIONS on the accent ladder and must never be
 * frozen into hues. Positions are also fixed PER CONCEPT — they are NOT re-spaced based on which OT
 * kinds a department happens to run, so a department without a consecutive-OT rule still reads the
 * same Daily/Weekly colors as one that has it.
 *
 * **`AGENCY` is deliberately OFF the ramp and NOT accent-derived** (APP-766 follow-up). Three
 * reasons, in order of weight. It is not a further step on the overtime scale — it is labor sourced
 * from outside the building, a different lever, so a categorical hue states the truth a 4th ramp
 * stop does not. It frees the ramp: three purple stops separate cleanly (1.87 / 4.15 / 6.68 contrast
 * against white) where four could not — the previous 200→900 span had to reach both extremes of the
 * ladder, leaving Daily invisible at 1.50 and Agency reading as black at 12.40. And it matches
 * `HOURS_SUMMARY.AGENCY`'s blue family, so agency finally reads as ONE concept across the charts and
 * the Hours Summary table. The value is chosen to differ from every purple stop in LIGHTNESS as well
 * as hue (8.86 against white), so it does not converge with `WEEKLY_OT` under deuteranopia — an
 * equal-lightness blue would. Being categorical, it is fixed across themes, exactly like
 * `HOURS_SUMMARY`.
 *
 * **Caveat — the OT stops are CSS-var strings, not hexes.** They resolve to the user's chosen accent
 * at paint time via the `--inhouse-primary-{shade}` ladder published by `ThemePreferenceProvider`, so
 * they cannot pass through MUI `alpha()`/`darken()`/`lighten()` (those parse a color string). Every
 * current call site hands them straight to a chart's `color`/`stroke`/`fill`, which is fine. If a
 * future call site needs a resolvable hex, follow the documented escape hatch above:
 * `STATUS_BADGE.BRAND_FALLBACK` / `BRAND_FALLBACK_HEX` is the var/hex pair for that case.
 *
 * Known outstanding adopter: `pages/PbjCalculator/PbjCalculatorData.tsx` still carries its own
 * labor-mix colors and was left out of APP-766's scope — migrate it here rather than extending it.
 */
export const LABOR_MIX = {
	/**
	 * In-house baseline hours. Labeled "Base" in Cost Deep Dive and "FULL TIME" in the PPD donut.
	 * Softer than the accent stops on purpose: this is the dominant series (~86% of a typical
	 * department's hours), so it has to RECEDE. A saturated fill at that share turns the chart into
	 * one green mass and buries the premium bands that are the reason to look.
	 */
	BASE: "#9FCFB4",
	DAILY_OT: "var(--inhouse-primary-300)",
	WEEKLY_OT: "var(--inhouse-primary-500)",
	CONSECUTIVE_OT: "var(--inhouse-primary-700)",
	/**
	 * Aggregate OT, for a chart that does NOT split the kinds (the PPD donut sums all of them into
	 * one slice). Deliberately the ramp's mid stop, so an unsplit slice sits where the split bands'
	 * center of mass would. It is a NAMED alias rather than a reuse of `WEEKLY_OT` because the two
	 * mean different things and only ever appear in different charts — never in the same one.
	 */
	OVERTIME_ALL: "var(--inhouse-primary-500)",
	/** Off-ramp categorical hue — see the `AGENCY` paragraph above before changing this. */
	AGENCY: "#0F4C81",
	/**
	 * Bonus and PTO are OFF the ramp on purpose: they are pay categories that happen to share the
	 * stack, not premium-escalation tiers, so they must not read as a step between Weekly and
	 * Agency. Categorical warm hues, relocated unchanged from `CostDeepDiveChart` — a relocation,
	 * not a restyle. They are also the two closest values in this block; if the Cost stack ever
	 * needs them told apart at a glance, re-tune them here rather than at the call site.
	 */
	BONUS: "#E8A838",
	PTO: "#F5A623",
	/**
	 * The stacked total. Dark neutral ink rather than a navy literal — `primary[900]` already
	 * resolves near-navy on the default brand and the two would collide. Follows the anchor-line
	 * convention at `MultiFacility/utils.ts` (`CHART_MULTI_FACILITY_AVERAGE = NEUTRAL[800]`), and is
	 * safe against the ramp because TOTAL is a thin stroke drawn over the fills, never another band.
	 */
	TOTAL: NEUTRAL[800],
} as const;

/**
 * Non-labor-mix chart reference series — the lines a labor-mix stack is READ AGAINST rather than
 * part of. Kept separate from `LABOR_MIX` because none of these is an hours category: scheduled vs
 * actual is a provenance pair, target is a threshold, census is a different axis entirely.
 *
 * `CENSUS` is opaque on purpose: it is drawn as a right-axis stroke, and the 70%-alpha variant some
 * charts used (`#95E2BAB3`) reads muddy at the 1.5px default stroke width.
 */
export const CHART_REFERENCE = {
	SCHEDULED: "var(--inhouse-primary-light)",
	ACTUAL: "#E07A5F",
	TARGET: "#66BB6A",
	/** Dashes the target line so a threshold never reads as measured data. Paired with `TARGET`. */
	TARGET_DASH: "5 5",
	CENSUS: "#95E2BA",
} as const;

/**
 * Scheduled-vs-Actual-Cost variance series. Two of the three are the provenance colors above,
 * because that is exactly what they mean: an unplanned cost came from the ACTUAL side, a no-punch
 * cost from the SCHEDULED side.
 *
 * `MISMATCH` is neutral slate rather than the base green it used to borrow — it is neither side, and
 * green is reserved for `LABOR_MIX.BASE` (APP-766).
 */
export const COST_VARIANCE = {
	UNPLANNED: CHART_REFERENCE.ACTUAL,
	NO_PUNCH: CHART_REFERENCE.SCHEDULED,
	MISMATCH: "#78909C",
} as const;

/**
 * Charge-nurse marker for the Coverage names grid (and any "who is charge"
 * indicator). A small teal "C" chip — distinct from the day/night shift hues
 * and from the coral OT badge, so charge reads as its own categorical role
 * signal rather than a status or shift type. Soft `BG` fill, saturated `INK`.
 * Stable across themes — role legibility must not shift with the accent color.
 */
export const CHARGE = {
	BG: "#D8EFEA",
	INK: "#1E7A66",
} as const;

/**
 * Third-party brand colors. These are governed by external trademark/brand
 * guidelines and must NOT be themed.
 */
export const BRAND = {
	GOOGLE: {
		BLUE: "#167EE6",
		GREEN: "#12B347",
		GREEN_DARK: "#0F993E",
		YELLOW: "#FFD500",
		RED: "#FF4B26",
		RED_DARK: "#D93F21",
	},
} as const;

/** Tints for soft surfaces (header chips, hover wash, banners). */
export const SURFACE_TINT = {
	LILAC_30: "rgba(216, 208, 232, 0.3)",
} as const;

/**
 * Categorical (functional) palette — the colour that tells one job, role, department or chart series
 * from another. Intentionally NOT theme-driven: it must stay stable when the user picks a new accent.
 *
 * Twelve slots, each in three shades generated from one OKLCH hue:
 *  - `DOT`  — 6–10px identity marks, bars, chart series.
 *  - `FILL` — pastel cell/chip backgrounds; always carries text or a dot, never identity on its own
 *             (pastels can't be pulled far enough apart to).
 *  - `INK`  — text/accent: ≥ 7:1 on white, ≥ 4.5:1 on its own fill.
 *
 * Entities take slots BY POSITION (see `utils/categoricalColors`), never by hashing a name — a hash
 * can put any two entities on the same hue. The first `CATEGORICAL_AUTO_SLOTS` are the automatic
 * order, seeded from Okabe-Ito (the colour-blind-safe standard) and tuned so that any six read apart
 * together, all eight stay apart for colour-blind readers, and none passes for the error, warning,
 * success, info or brand colour. The last four are used once a department has more jobs than that,
 * and are offered as manual picks. Every one of
 * those rules is pinned by `categoricalPalette.spec.ts` — re-run it after touching a hex.
 */
export const CATEGORICAL = {
	DOT: ["#774fb9", "#e39f0e", "#5c9cfe", "#cf5da2", "#3acc7a", "#8c5404", "#28c3d5", "#8c8f24", "#87305f", "#085b87", "#e293ab", "#48a689"],
	FILL: ["#d9d3e9", "#fcd191", "#e0ecfe", "#feb5dd", "#beeecb", "#ffe1c4", "#a0f3ff", "#e4e89c", "#ffd3e6", "#c6e6ff", "#ffd4e0", "#baefdb"],
	INK: ["#614691", "#755000", "#2a579a", "#853867", "#056637", "#7d4a02", "#04616b", "#585a02", "#873761", "#0e5e8a", "#833e56", "#03654e"],
} as const;

/** How many slots automatic assignment cycles through; the rest are offered only as manual picks. */
export const CATEGORICAL_AUTO_SLOTS = 8;

/** Chart series colours: the automatic slots, in order — series N takes slot N. */
export const CATEGORICAL_SERIES: readonly string[] = CATEGORICAL.DOT.slice(0, CATEGORICAL_AUTO_SLOTS);

/** Number of selectable categorical slots — the valid range of a stored colour slot is `0 … N-1`. */
export const CATEGORICAL_SLOT_COUNT = CATEGORICAL.DOT.length;

export const CATEGORICAL_DEPARTMENT = {
	Hospital: "#90CAF9",
	SNF: "#FDD5D9",
	ALF: "#B59BE5",
} as const;

/**
 * Common alpha overlays for shadows / scrims / focus rings. Resolve against
 * `theme.palette.primary.main` at the call site via MUI's `alpha()` helper —
 * do not hardcode rgba literals in feature files.
 */
export const ALPHA = {
	SHADOW_SOFT: 0.08,
	SHADOW_MEDIUM: 0.16,
	SHADOW_HEAVY: 0.24,
	HOVER_OVERLAY: 0.04,
	SELECTED_OVERLAY: 0.08,
	FOCUS_RING: 0.32,
	BACKDROP: 0.5,
	/** Background opacity for a brand-tinted icon tile (sits over a white panel). */
	BRAND_TINT: 0.1,
	/** Background wash for an inline hint surface (sits over a white card, behind body text). */
	HINT_TINT: 0.08,
	/** Muted secondary text sitting over a tinted header (e.g. a week's date range). */
	MUTED_SECONDARY: 0.78,
	/** A struck / de-emphasized element (e.g. a PTO'd nurse's marker). */
	STRUCK: 0.45,
	/**
	 * White-preview fill opacity for the hovered inactive option of a segmented
	 * control (`SystemFlatSwitch`). Sits over the neutral track and previews where
	 * the active pill would land, signalling the option is clickable.
	 */
	SEGMENT_HOVER_FILL: 0.6,
	/** Unlit bar of a level meter (`UIToolkit/Inputs/InlineLevelPicker`) — present but clearly "off". */
	METER_EMPTY_BAR: 0.22,
	/** Brand tint of a small inline control on hover / while its menu is open (one step above `SELECTED_OVERLAY`). */
	CONTROL_TINT_HOVER: 0.16,
	/**
	 * Brand wash painted diagonally behind a page/panel identity hero (a detail page's name + avatar band,
	 * a card's title band). Deliberately below `HINT_TINT`: it has to read as depth under a heading, not as
	 * a tinted callout, and body text sits on it. Derive it from `theme.palette.primary.main` so the wash
	 * re-tints when the org changes its accent.
	 */
	HERO_WASH: 0.07,
	/**
	 * An already-completed segment of a `SystemStageTrack`. A notch under the current segment's solid tone
	 * so "done" reads as settled rather than competing with "you are here".
	 */
	STAGE_DONE: 0.5,
	/**
	 * A status-tinted hairline border on a card/callout that needs attention (e.g. a location card whose
	 * rows disagree). Resolve against the relevant `theme.palette.{warning,error,…}.main` via `alpha()` —
	 * strong enough to read as "flagged" against a neutral field, quiet enough not to shout like a fill.
	 */
	STATUS_BORDER: 0.4,
	/**
	 * The dotted underline under a name that is not a link but opens a hover card (`HoverCard`) — "there is
	 * more here", without dressing it as a link. Resolve against `theme.palette.primary.main`; half-strength so
	 * it reads as a cue beside real links, not as one of them.
	 */
	CUE_UNDERLINE: 0.5,
} as const;

/** Tokenized box-shadows for elevated surfaces — use instead of hardcoding `box-shadow` rgba literals. */
export const SHADOW = {
	/** Soft low-elevation lift for a control panel sitting on a neutral ground. */
	PANEL_SOFT: "0px 1px 2px rgba(16, 24, 40, 0.04), 0px 2px 8px rgba(16, 24, 40, 0.04)",
	/** Subtle lift for a small raised control on a tinted track (e.g. the active segment of a segmented toggle). */
	CONTROL_RAISED: "0px 1px 2px rgba(16, 24, 40, 0.08)",
	/** Slightly deeper lift on hover for an interactive panel. */
	PANEL_SOFT_HOVER: "0px 2px 4px rgba(16, 24, 40, 0.06), 0px 4px 12px rgba(16, 24, 40, 0.06)",
	/** Brand-purple-tinted drop shadow for a page header bar separating it from the scroll body. */
	HEADER_BRAND: "0px 4px 4px 0px rgba(129, 123, 177, 0.2)",
	/** High-elevation lift for a floating non-modal window (chat window, palette) hovering over the page. */
	FLOATING_WINDOW: "0px 12px 32px rgba(16, 24, 40, 0.16), 0px 2px 8px rgba(16, 24, 40, 0.08)",
	/**
	 * Right-edge lift on the last frozen (sticky-left) column of a scrolling grid —
	 * makes the frozen identity zone read as one layer over the day cells sliding
	 * under it. Shared by every Master Schedule v3 grid (was duplicated per-file).
	 */
	STICKY_EDGE: "6px 0 6px -6px rgba(16, 24, 40, 0.10)",
	/**
	 * Trailing right-edge fade overlay signalling that MORE day columns exist
	 * off-screen when a grid overflows horizontally (tablet/mobile discoverability).
	 * A gradient (not a box-shadow) painted over the cells — the sticky-left
	 * STICKY_EDGE lift can't be seen through the opaque cells, so the trailing hint
	 * is an on-top overlay. Shares STICKY_EDGE's neutral ink so both edges read as
	 * one elevation family. Fades in only while there is content still to scroll.
	 */
	SCROLL_HINT_RIGHT: "linear-gradient(to left, rgba(16, 24, 40, 0.14), rgba(16, 24, 40, 0))",
	/**
	 * Neutral ink for a pure-CSS scroll-shadow edge fade (Lea Verou
	 * `background-attachment: local, scroll` technique) painted on an overflowing
	 * scroll container — e.g. a wide chat-markdown table. Auto-appears only while
	 * there is content off-screen and hides at the ends / when the content fits,
	 * with no JS or scroll listeners. Shares the grid scroll-hint ink family so
	 * every "more content this way" affordance reads as one elevation family.
	 */
	SCROLL_SHADOW_INK: "rgba(16, 24, 40, 0.16)",
	/** Elevation for a floating popover/menu surface detached from the page (e.g. the period controls popover). */
	POPOVER_FLOAT: "0px 12px 32px rgba(16, 24, 40, 0.16), 0px 4px 12px rgba(16, 24, 40, 0.06)",
	/**
	 * The one UPWARD-casting shadow: a bar pinned to the bottom of the viewport (the phone bottom nav)
	 * lifting off the content that scrolls beneath it. Every other shadow here casts downward, which on
	 * a bottom bar reads as the bar being *under* the page — the light has to come from the same place
	 * for the elevation to read at all. Same neutral ink family as the panel shadows, so one elevation
	 * language: a tight contact line plus a wide, soft ambient spread.
	 */
	BAR_TOP: "0px -1px 2px rgba(16, 24, 40, 0.04), 0px -8px 24px rgba(16, 24, 40, 0.08)",
} as const;

/**
 * Corner radius (px) for surfaces addressed in plain CSS — e.g. the `react-tiny-popover`
 * root style, which receives a `CSSProperties` object where MUI `sx` theme units aren't
 * available. For MUI `sx`, prefer `theme.shape.borderRadius` multiples instead.
 */
export const RADIUS = {
	/** Floating card/popover surface — matches the redesigned page's soft cards. */
	POPOVER: 16,
	/**
	 * The canonical surface radius — the same 8px `theme.shape.borderRadius` resolves to, for the
	 * plain-CSS call sites that can't reach the theme. In `sx` write `borderRadius: 1` instead.
	 */
	CARD: 8,
	/** A bordered control card (a settings row, a switch card). */
	CONTROL: 12,
	/** A picture tile (`VisualChoiceGrid`). */
	TILE: 14,
	/** A focal hero panel (the Annie Studio stage). */
	HERO: 24,
} as const;

/**
 * Motion primitives — the single source of truth for animation timing/curves. CSS values (`EASE_OUT`,
 * most `DURATION.*`) drive predetermined mount/hover CSS transitions; the Framer Motion fields
 * (`EASE_IN_OUT`/`EASE_OUT_POINTS` bezier tuples, `VIEW_*`) drive the handful of dynamic Framer transitions, since those
 * numbers are visual values too and shouldn't be hardcoded at the call site.
 */
export const MOTION = {
	/** Ease-out curve for entrances and hover lifts (transform/opacity only). CSS-transition string form. */
	EASE_OUT: "cubic-bezier(0.23, 1, 0.32, 1)",
	/** Gentle ease-in-out as a cubic-bezier tuple for Framer Motion (which takes the array form, not the CSS string). */
	EASE_IN_OUT: [0.4, 0, 0.2, 1] as [number, number, number, number],
	/** The same ease-out curve as control points, for Framer Motion (which rejects the CSS `cubic-bezier(...)` string). */
	EASE_OUT_POINTS: [0.23, 1, 0.32, 1],
	/**
	 * Framer spring for a figure that has to be readable at a glance while it moves (a count that follows a
	 * choice made right beside it): settles in ~250ms with no overshoot, where the default count-up spring
	 * takes over a second and can be screenshotted, or read, mid-flight.
	 */
	SPRING_SETTLE: { stiffness: 420, damping: 42 },
	/** Swing (px) of the `DURATION.SHAKE` head-shake — small enough to stay in the field's own footprint. */
	SHAKE_OFFSET_PX: 4,
	/** Entering-list stagger (ms): per-item `STEP` delay, capped at `MAX` — the 30–80ms house window. */
	STAGGER: { STEP: 40, MAX: 80 },
	/** Durations (ms) for short, predetermined animations. */
	DURATION: {
		HOVER: 160,
		/** Small control state change (checkbox fill, selected-row tint) — snappier than a hover. */
		CONTROL: 140,
		ENTRANCE_SM: 180,
		ENTRANCE_MD: 200,
		/**
		 * An inline note collapsing away (`InlineHint` with `open`) — paired with `ENTRANCE_SM` and
		 * snappier than it, the same asymmetry the window pair below uses: the user deliberates over
		 * showing something, the system should feel prompt taking it back.
		 */
		INLINE_EXIT: 140,
		/** Guided opening content exiting into the first chat turn. */
		OPENING_EXIT: 220,
		/** Floating window (chat/palette) enter — an occasional surface, so a touch slower for a deliberate entrance. */
		WINDOW_ENTER: 240,
		/** Floating window exit — snappier than enter (asymmetric timing: the system responds faster than the user deliberates). */
		WINDOW_EXIT: 160,
		/** Management Center grid ⇄ charts crossfade (Framer): an unhurried, gentle enter. */
		VIEW_ENTER: 340,
		/** …and a snappier exit than the enter. */
		VIEW_EXIT: 240,
		/** Micro color/border/box-shadow crossfade (ms) — hover tints, selected-state washes, slider-thumb halo. */
		COLOR_SHIFT: 200,
		/** In-place dim/undim fade (ms) for a busy overlay that freezes its children while recomputing. */
		OVERLAY_FADE: 150,
		/** One-shot "land pulse" (ms) — a brief halo ring on a control the system just repositioned. */
		PULSE: 320,
		/**
		 * One-shot "pop" (ms) for a small badge/marker arriving — scales up through a slight overshoot
		 * and settles. Longer than `ENTRANCE_SM` because the overshoot needs room to read as a bounce
		 * rather than a stutter, and still inside the sub-300ms UI budget.
		 */
		POP: 260,
		/** One-shot bouncy "spotlight jump" (ms) for a freshly-added list item, so the eye catches the new one. */
		HIGHLIGHT_JUMP: 600,
		/**
		 * One-shot "not yet" shake (ms) on a required field the user tried to skip (a removal's reason): long enough
		 * for four swings to read as a head-shake, not a jitter.
		 */
		SHAKE: 400,
		/**
		 * Long "instrument fill" sweep (ms) — the gauge arc, hero progress bar, and linear
		 * meter all fill in lockstep at this duration so the instrument family reads as one
		 * synchronized motion. Longer than the UI entrance curves on purpose: this is a
		 * deliberate data reveal, not a chrome transition.
		 */
		INSTRUMENT_FILL: 900,
		/**
		 * Filter rail collapse/expand (ms) — the container's `width` transition, with its content
		 * cross-fading over a shorter slice of it.
		 *
		 * Above the 200–500ms drawer window on purpose: this one moves the table beside it, so the eye
		 * follows a layout change rather than a panel appearing, and at drawer speed that reads as a
		 * snap. Tuned by hand — 420ms read as instant, 1000ms as sluggish.
		 */
		RAIL_TOGGLE: 700,
		/**
		 * "Which group did I just land on?" flash (ms) on a filter group jumped to from elsewhere.
		 *
		 * Deliberately long — this is not a chrome transition but a wayfinding signal that has to
		 * survive a smooth scroll finishing before the eye arrives. Short flashes are over before the
		 * user has looked, which reads as nothing having happened at all.
		 */
		FACET_FLASH: 2400,
		/**
		 * "This is the rule you came for" wash (ms) on a config-table row a deep link landed on (the wage
		 * breakdown's rate type → its Wage Settings rule). Same wayfinding job as `FACET_FLASH`, a touch
		 * shorter because the row is a smaller, denser target that the scroll has already centred on.
		 */
		ROW_HIGHLIGHT: 2000,
	},
	/** Waits (ms) before a transient surface reacts to the pointer. */
	DELAY: {
		/**
		 * A hover card (`UIToolkit/Popups/HoverCard`) opening over a name. Long enough that sweeping the
		 * pointer across a list of names does not flash a card for each one, short enough that resting on
		 * one reads as an answer rather than a wait.
		 */
		HOVER_CARD_ENTER: 350,
		/** …and closing once the pointer leaves: a beat to cross the gap onto the card, so it can be scrolled. */
		HOVER_CARD_LEAVE: 120,
		/**
		 * A full-screen loader showing its indicator (`Maintenance/MaintenanceLoadingScreen`). A wait under this passes as
		 * one frame of background; showing a loader for it reads as a flash. Around the point a wait starts to be noticed.
		 */
		LOADER_REVEAL: 300,
	},
	/** Slide distance (px) for the grid ⇄ charts crossfade; forced to 0 under reduced motion. */
	VIEW_SLIDE_PX: 8,
} as const;

/**
 * Geometry for a hover card (`UIToolkit/Popups/HoverCard`) — a read-only preview that floats over a name,
 * e.g. the configuration rows behind a wage breakdown line.
 */
export const HOVER_CARD = {
	/**
	 * Its widest. A settings row can run to a dozen columns, so the card scrolls sideways inside this
	 * rather than widening past the viewport (the `100vw` term keeps a margin on a phone).
	 */
	MAX_WIDTH: "min(60rem, calc(100vw - 2rem))",
	/** Its narrowest: a one-message card (loading, a removed rule) still reads as a card, not a tooltip. */
	MIN_WIDTH: "16rem",
	/** Its tallest: a segment with several rules scrolls inside the card instead of running off-screen. */
	MAX_HEIGHT: "min(30rem, 70vh)",
	/**
	 * Its corners: between a card (`RADIUS.CARD`) and a popover (`RADIUS.POPOVER`) — it floats like a
	 * popover but holds tables, whose own 8px corners sit inside it. A CSS length, so `sx` does not scale it.
	 */
	RADIUS: "12px",
} as const;

/** Geometry (px) for the standard faceted filter sidebar (`UIToolkit/Filters/FilterSidebar`). */
export const FILTER_SIDEBAR = {
	/**
	 * The collapsed rail's width (`collapsedVariant="rail"`). Wide enough for a 18px glyph, a count
	 * badge and a vertical label, narrow enough that collapsing actually buys the table its width back.
	 */
	COLLAPSED_WIDTH: 44,
	/** Width of the expanded sidebar panel. */
	WIDTH: 264,
} as const;

/** Layout offsets (px) shared across settings/config layouts. */
export const LAYOUT_OFFSET = {
	/** Top offset (px) for a sticky settings label column so it clears page chrome. */
	SETTINGS_STICKY_TOP: 24,
	/** Min height (px) of a settings/config page header bar (title + actions row). */
	PAGE_HEADER_MIN_HEIGHT: 64,
	/**
	 * How far a mobile sheet allowed to grow to full height stops below the top of the screen: the status
	 * bar's safe area plus a strip of the page, so it still reads as a sheet over the screen, not a new page.
	 * `0px` is `env()`'s required fallback inside `calc()`.
	 */
	SHEET_TOP_GAP_CSS: "calc(24px + env(safe-area-inset-top, 0px))",
} as const;

/** Max content widths (px) for centered single-column reading layouts (profiles, detail pages). */
export const CONTENT_WIDTH = {
	/**
	 * A comfortable single-column reading width for a centered detail page (candidate profile, record
	 * detail). Pair with `mx: "auto"` + `width: 100%` so the column fills small screens but stops widening
	 * on desktop instead of hugging the left edge.
	 */
	READABLE: 940,
	/**
	 * A wider centered ceiling for a data-dense page whose cards hold tables/grids (e.g. the Locations
	 * page). One step above `READABLE`: still stops hugging the left edge on desktop, but leaves a
	 * multi-column grid room to breathe. Pair with `mx: "auto"` + `width: 100%`.
	 */
	WIDE: 1200,
	/**
	 * The measure a paragraph of SECONDARY-size copy reads at — a banner's explanation, the helper text
	 * under a control. `READABLE` is sized for 15–16px body type; at 13px that same 940px runs ~145
	 * characters a line and the eye loses the return sweep. Cap the prose with this, not the container,
	 * when the surface also holds something that wants the full width (a list, a grid).
	 */
	ANNOTATION: 680,
	/**
	 * The column a short announcement reads in when it floats over the page rather than sitting in the
	 * layout (a notice popover, a callout). Two or three lines of a sentence: wider and a two-line note
	 * becomes one long ribbon the eye has to track back across, narrower and it turns into a column of
	 * three-word rows. Sits inside a phone viewport once the popover's own inset and padding are paid.
	 */
	FLOATING_NOTICE: 300,
	/**
	 * A single card that IS the screen — an outage screen, a one-question gate before the app. Holds a full-page
	 * `EmptyState` (`EMPTY_STATE.PAGE_TEXT_MAX_WIDTH`) plus its side padding, and stops there so a desktop reads it as
	 * one calm card rather than a banner across the page.
	 */
	SCREEN_CARD: 560,
} as const;

/**
 * Geometry for an itemized ledger (`UIToolkit/molecules/LedgerLine`) — a list whose figures must line up
 * in columns so it can be read as a sum (a wage breakdown's segments and the rules that built each rate).
 *
 * Every width is in `rem`, never `ch` or `em`: a ledger mixes line sizes (items at body size, components a
 * notch smaller), and a font-relative column would come out a different width on each size — the figures
 * of an item and of the components summing to it would stop sharing a right edge. The widths fit the
 * widest figure a line shows ("+$1,234.56/h", "$1,234.56") at the item size.
 */
export const LEDGER = {
	/** The leading glyph slot. Kept on lines without a glyph too, so every label starts on one edge. */
	LEADING_WIDTH: "1.25rem",
	/** The secondary figure column (a rate, a unit price), unit slot included. */
	RATE_COLUMN_WIDTH: "6.75rem",
	/**
	 * The slot a rate's unit ("/h") trails in. Reserved on every line that has a rate, unit or not, so a
	 * "$55.96/h" and the "+$10.50" lines summing to it keep their digits in one column.
	 */
	RATE_UNIT_WIDTH: "1.5rem",
	/** The primary figure column (the line's amount). */
	AMOUNT_COLUMN_WIDTH: "5.5rem",
	/** How far a component line sits in from its parent — past the glyph, to hang off the rail. */
	COMPONENT_INDENT: "1.75rem",
	/**
	 * An item's label column, wide enough for the longest segment name the wage breakdown generates
	 * ("Holiday Consecutive OT"), so the details after "Day" and after "Day Weekly OT" start on one edge.
	 * Only a free-text name on a legacy breakdown can be longer; it pushes its own line rather than being cut.
	 */
	ITEM_LABEL_MIN_WIDTH: "12rem",
	/** The hairline rail (px) a parent's component lines hang from. */
	RAIL_WIDTH: 2,
	/** The rail's tint over the brand color: a structural guide, quieter than a divider. */
	RAIL_ALPHA: 0.22,
} as const;

/**
 * Geometry (px) for a multi-step wizard dialog (`UIToolkit/Popups/SystemWizardDialog`).
 *
 * A wizard's steps hold wildly different amounts of content — two fields, then a grid — so without a
 * floor the modal resizes and re-centres on every advance and the footer buttons walk up and down the
 * screen under the cursor. The floor is what makes the sequence read as one surface being filled in
 * rather than a series of unrelated popups.
 */
export const WIZARD_DIALOG = {
	/** Minimum height of the step body, so the modal keeps its footprint between steps. */
	BODY_MIN_HEIGHT: 300,
} as const;

/**
 * The approval popup (`StyledDialog`): its paper is capped at this height. A content-sized panel inside it — the
 * recommendation's Remove Staff — takes the same value as its ceiling, so it can never push its own footer below
 * the fold.
 */
export const APPROVAL_DIALOG = {
	MAX_HEIGHT: "80vh",
} as const;

/**
 * Heights (px) for interactive controls, so anything sharing a row shares a centre line.
 *
 * Before this scale existed every control was whatever its MUI primitive or its author's `sx`
 * happened to produce: the weekly staffing screen rendered twelve distinct control heights, and the
 * misalignments that came out of that (a 2px-low Search Staff field, a 4px-low tab strip, a header
 * whose hit targets ranged 24-40px) each got fixed as its own one-off. Reach for a step here instead
 * of typing a number — a row can only line up if something says what a row of controls measures.
 *
 * Two spellings of ONE scale. `CONTROL_HEIGHT` is the CSS length for `sx`; `CONTROL_HEIGHT_PX` is the
 * same step as a NUMBER, for layout arithmetic — a height budget, a reserved zone width, an
 * `n * step` span. A view doing that math used to have to re-type the literal (the monthly grid
 * carried its own `CELL_HEADER_HEIGHT = 24` and a `12 + 24` chrome budget), which silently decoupled
 * its row-fitting from the control it was measuring. Parse no strings; reach for the numeric twin.
 */
export const CONTROL_HEIGHT_PX = {
	/**
	 * An action sitting *inside* content rather than in chrome — the copy/regenerate/rate row under a
	 * chat answer. Deliberately under `TOUCH_TARGET_MIN`, so pair it with `TOUCH_TARGET_SX`.
	 */
	MICRO: 24,
	/** A dense control inside another control, or a toolbar that has to fit more than it should. */
	COMPACT: 32,
	/**
	 * The standard field height. Selects, pill buttons and MUI's small outlined input all already
	 * measure this, so anything shorter on the same row resolves its centre lower and reads as
	 * misaligned.
	 */
	STANDARD: 40,
	/** A touch-first control: comfortably above `TOUCH_TARGET_MIN` on a tablet or touch-capable laptop. */
	LARGE: 48,
	/** The one deliberately oversized step: a page's single primary call to action. */
	XL: 56,
} as const;

/**
 * The same scale as CSS lengths — what you hand to `sx`. Derived from `CONTROL_HEIGHT_PX` rather than
 * restated, so the two spellings of one measurement cannot drift apart.
 */
export const CONTROL_HEIGHT = {
	MICRO: `${CONTROL_HEIGHT_PX.MICRO}px`,
	COMPACT: `${CONTROL_HEIGHT_PX.COMPACT}px`,
	STANDARD: `${CONTROL_HEIGHT_PX.STANDARD}px`,
	LARGE: `${CONTROL_HEIGHT_PX.LARGE}px`,
	XL: `${CONTROL_HEIGHT_PX.XL}px`,
} as const;

/**
 * A muted icon-only button that sits at the end of a line of text (`UIToolkit/Clickable/GlyphButton`) — e.g. the glyph
 * after a request's title that opens why it was posted. Sized to a line of text, not a toolbar: a `MICRO` box with a
 * glyph a notch under `ICON_SIZE.INLINE`, so it adds no height to the line it ends.
 */
export const GLYPH_BUTTON = {
	SIZE_PX: CONTROL_HEIGHT_PX.MICRO,
	GLYPH: "17px",
	/** The count badge in its top-trailing corner, ringed in the surface colour so it reads as cut out of the glyph. */
	BADGE_SIZE_PX: 15,
	BADGE_OFFSET_PX: -5,
	BADGE_RING_PX: 2,
	BADGE_FONT: "0.59375rem",
} as const;

/**
 * `NamedTimeRangeListField` rows. A row puts its From–To pair, its name and its remove action on one line once the
 * list is this wide; narrower (a settings side panel) the name and remove action drop to a second line under the range.
 * A container width, not a viewport breakpoint: the same list sits in resizable panels.
 */
export const NAMED_TIME_RANGE_ROW = {
	SINGLE_LINE_MIN_WIDTH_PX: 560,
	/**
	 * Under this list width the From/To pair stacks. Side by side, each field keeps ~40px of right padding for
	 * its clear icon, so a 280px panel leaves room for "7:…" and not "3:00 PM (8h0m)".
	 */
	STACKED_RANGE_MAX_WIDTH_PX: 340,
	/** Under this list width (the panel dragged to its minimum) the remove action moves down beside the name, giving the times the full width. */
	REMOVE_BESIDE_NAME_MAX_WIDTH_PX: 200,
} as const;

/** Line heights for small explanatory text — a label on one line, a sentence that wraps, a paragraph. */
export const LINE_HEIGHT = {
	TIGHT: 1.3,
	SNUG: 1.4,
	RELAXED: 1.5,
} as const;

/**
 * An event list (`UIToolkit/molecules/EventList`). The `timeline` variant hangs each step's tile on a rail: a connector
 * runs from under one tile to the next, `CONNECTOR_GAP_PX` clear of both, so the steps read as "this led to that".
 * `STEP_PY` (theme spacing) gives each step the room a rail needs to read as a line rather than a divider.
 */
export const EVENT_LIST = {
	STEP_PY: 1.25,
	CONNECTOR_PX: 2,
	CONNECTOR_GAP_PX: 4,
	/** The latest step's tile carries a soft halo of this width — "this is where it stands now". */
	EMPHASIS_RING_PX: 3,
} as const;

/**
 * An explainer card (`UIToolkit/Panels/ExplainerCard`): "why is this here", one sentence and then the facts. The width
 * is its reading measure inside a `HoverCard` — wide enough for a sentence on two lines, narrow enough to stay a card.
 */
export const EXPLAINER_CARD = {
	WIDTH: "21.5rem",
} as const;

/**
 * A value-vs-target meter (`UIToolkit/DataViz/TargetGapMeter`). The track runs a little past the larger of the two, so
 * the target tick never sits on the rounded end cap where it would read as the bar's edge.
 */
export const TARGET_GAP_METER = {
	HEADROOM: 1.05,
	HEIGHT_PX: 8,
} as const;

/**
 * Geometry for a stat tile in a KPI row (`ClickableStatTile`).
 *
 * A row of tiles only reads as a row if the tiles are the same size regardless of how long their
 * labels are, so both floors live here rather than at the call site.
 */
export const STAT_TILE = {
	/** Enough height for an uppercase label line plus a `FONT_SIZE.STAT_VALUE` figure under it. */
	MIN_HEIGHT: 78,
	/** Below this a two-word label wraps and the tile stops matching its neighbours' height. */
	MIN_WIDTH: 160,
	/** The active tile's left accent rail. */
	ACCENT_RAIL_WIDTH: 3,
} as const;

/**
 * Data-grid row heights (px).
 *
 * A roster of 300 staff and a five-row settings table want different densities, and before this the
 * choice was a bare number typed per grid — the department metrics table carried its own local
 * `COMPACT_ROW_HEIGHT = 36`, and everything else silently inherited MUI's compact default. Pass one
 * of these to `SystemDataGrid`'s `rowHeight` so density is a decision with a name, and so changing
 * what "compact" measures is one edit rather than a search.
 */
export const GRID_ROW_HEIGHT = {
	/**
	 * Long lists people scan rather than read — a staff roster, a shift log. Fits roughly sixteen rows
	 * on a laptop where the default fits ten, which is the difference between paging and scanning.
	 */
	COMPACT: 36,
	/** The default: a table of a few dozen rows read one at a time. */
	STANDARD: 44,
	/** Rows carrying two lines of text, or a control taller than a chip. */
	RELAXED: 56,
} as const;

/**
 * Glyph sizes (px) for icons sitting inside interactive controls.
 *
 * A row of equal-sized boxes still reads unevenly if the ink inside them differs: the top bar's
 * controls were all 40px yet their glyphs ranged 20-24px, which is 8px vs 10px of internal padding,
 * which is what the eye measures the gap from. Pair this with `CONTROL_HEIGHT` — same box, same glyph,
 * and the gap between two controls is then the only thing setting their spacing.
 */
export const ICON_SIZE = {
	/** The standard control glyph — matches MUI's `fontSize="small"`. */
	CONTROL: "20px",
	/** The glyph for a `CONTROL_HEIGHT.MICRO` box — 20px would fill a 24px control edge to edge. */
	MICRO: "15px",
	/** A glyph sitting inline with body2 text or a small button's label (a disclosure chevron, a heading's marker). */
	INLINE: "18px",
	/**
	 * 18px — the glyph on a full-width menu or sheet row, where the LABEL is what the eye reads and the
	 * icon is a marker beside it. `CONTROL` (20px) is sized to carry a button on its own, which on a row
	 * pulls attention away from the words.
	 */
	MENU_ROW: "18px",
	/**
	 * 14px — the glyph inside a compact pill (`CONTROL_HEIGHT.COMPACT`) beside a `LABEL_SM` label, e.g. a filter pill's
	 * funnel. `MICRO` (15px) is sized to fill a 24px control on its own; beside a label it crowds the words.
	 */
	PILL: "14px",
} as const;

/**
 * The `SystemChips` `variant="outlined-brand"` pill — a toolbar-height single-choice filter ("All shifts 9",
 * "☀ 7:00 AM–3:00 PM 6") that sits on one row with a search field and the Sort / Columns triggers, so it
 * shares their `CONTROL_HEIGHT.STANDARD`. Washes are `alpha(theme.palette.primary.main, x)`, so the pill
 * re-tints with the org's brand.
 */
export const FILTER_CHIP = {
	/** Under the selected pill — over white it lands on the design's pale lilac (≈ #ECEBF4 at the default brand). */
	SELECTED_WASH_ALPHA: 0.14,
	/** The selected pill under the pointer: one notch deeper, so hover still reads on the live choice. */
	SELECTED_HOVER_WASH_ALPHA: 0.18,
	/** The count badge on an unselected pill — the brand-tint tile wash, so the number reads as the brand's. */
	COUNT_TINT_ALPHA: ALPHA.BRAND_TINT,
	/** A leading glyph (a sun / moon) beside a `FONT_SIZE.EMPHASIS` label. */
	ICON_SIZE: "16px",
	/** `:active` press — the house button press. */
	PRESS_SCALE: 0.97,
	/** Focus ring spread (px), drawn with `box-shadow` so it never shifts the row. */
	FOCUS_RING_PX: 3,
	/** The brand dot (px) leading a pill's note ("• 2 picked") — a status mark, quieter than the count badge. */
	NOTE_DOT_SIZE: 6,
} as const;

/** Widths (px) for short numeric inputs, so the same kind of field is sized the same everywhere. */
export const NUMERIC_FIELD_WIDTH = {
	/** A few digits sitting next to a suffix label, e.g. "[24] hours before shift start". */
	INLINE: 88,
	/** A few digits standing alone in a settings row, where the row label carries the text. */
	SETTINGS: 120,
	/** `SETTINGS` plus room for a leading `$`/`%` adornment, which eats the padding digits need. */
	SETTINGS_WITH_UNIT: 140,
	/** A 24-hour "HH:mm" clock field with its floating label (a call-in's time beside its date). */
	CLOCK: 110,
} as const;

/** Widths (px) for short free-text inputs, so the same kind of field is sized the same everywhere. */
export const TEXT_FIELD_WIDTH = {
	/** An external system's identifier, e.g. a "NUR-1612464220" marketplace facility id. Wide enough
	    that a full vendor id is readable without scrolling — the numeric widths truncate them. */
	IDENTIFIER: 320,
	/**
	 * A date or a time field sharing one row with its partner (a call-in's date and clock): the width either may shrink
	 * to before the pair wraps to stacked, since a date field has an intrinsic width it will not go below and a fixed
	 * side-by-side row overflows a 320px phone.
	 */
	PAIRED_MIN: 160,
} as const;

/** Widths (px) for an anchored settings popover, so panels of one kind open at one size. */
export const POPOVER_PANEL_WIDTH = {
	/** A checklist of labelled boxes with a sentence of explanation above it, e.g. the Hiring page's
	    "What counts as an absence?". Room for a reason label and its box on one line in every language. */
	CHECKLIST: 360,
} as const;

/** Geometry (px) for the small inline tab/label indicator — a count pill or a status dot. */
export const BADGE = {
	/** Diameter of the bare status dot. */
	DOT_SIZE: 8,
	/** A status dot INSIDE a chip — a notch under `DOT_SIZE`, since the chip's fill already carries
	    the tier. Keep it ringed: on a pale fill the hairline is what gives it an edge. */
	DOT_SIZE_SM: 6,
	/** A legend swatch and nothing more. The floor: the ring is 1px at any diameter, so below 5px it
	    is more of the swatch than the tier's own colour. */
	DOT_SIZE_XS: 5,
	/** Min width / height of the count pill (square-ish at single digits). */
	PILL_SIZE: 18,
	/** Corner radius of the count pill (half its height → fully rounded). */
	PILL_RADIUS: 9,
	/** Radius (px) that reads as a fully-rounded pill at any height (clamps to half-height). */
	PILL_RADIUS_FULL: 999,
	/** Height of the employee-status chip (MUI Chip) — small / medium. Larger than the count pill. */
	STATUS_CHIP_HEIGHT_SM: 22,
	STATUS_CHIP_HEIGHT_MD: 26,
	/** Height of a compact inline tag/flag chip inside a dense table (N/A, Invalid, Manual, tier). */
	TAG_CHIP_HEIGHT_SM: 18,
	/** Height of a slightly larger interactive settings chip (e.g. the census affordance chip). */
	TAG_CHIP_HEIGHT_MD: 24,
} as const;

/**
 * A person's role badge (`ColorfulRole`) on the gaps-first phone view (`staffing/mobileGaps/GapsRoleBadge`), at its two
 * scales. One geometry per scale, so a person reads the same on a roster row, in a sheet's list and on an applicant line.
 */
export const ROLE_BADGE = {
	/** In a list row — the home's roster rows and every sheet or card that lists people. The dense tag chip's height. */
	ROW: { HEIGHT: BADGE.TAG_CHIP_HEIGHT_SM, RADIUS: GRID_RADIUS.BADGE },
	/** The leading badge of a sheet heading about one person — a size up, so it holds the heading's line. */
	HEADING: { MIN_WIDTH: 36, HEIGHT: BADGE.TAG_CHIP_HEIGHT_MD, RADIUS: GRID_RADIUS.CHIP },
} as const;

/**
 * Geometry (px) for a decorative celebration/easter-egg overlay (`UIToolkit/Animations/UnlockBurst`)
 * — sized to read as a small "pop" near the control that triggered it (a toggle, a badge) without
 * dominating it or disturbing the row's layout (the overlay is always absolutely positioned).
 */
export const UNLOCK_BURST = {
	/** Default overlay size (width/height, square) in px. */
	DEFAULT_SIZE: 64,
} as const;

/**
 * Geometry (px) for the ascending-bar level meter and its pill trigger
 * (`UIToolkit/Inputs/InlineLevelPicker`) — the low→high instrument glyph that encodes a scale
 * without spending a label per step.
 */
/**
 * Smallest tappable edge, in px — WCAG 2.5.5 and the iOS HIG both land on 44. A FLOOR for hit areas,
 * not a control height: `CONTROL_HEIGHT.STANDARD` (40px) is still the alignment unit for the field
 * family, so growing a control past this is opt-in per call site (see `UIToolkit/touchTarget`).
 */
export const TOUCH_TARGET_MIN = 44;

/**
 * Height (px) of a status accent strip drawn along the edge of a global chrome surface — the quiet
 * "a view-wide mode is on" reminder (e.g. the header while wages are hidden). Thick enough to register
 * as deliberate rather than an anti-aliasing artifact, thin enough that it never competes with the bar
 * it hangs off. Paint it as a pseudo-element so switching the mode costs no layout.
 */
export const ACCENT_STRIP_HEIGHT = 3;

/**
 * White-overlay opacities for a control drawn directly on the app's colored chrome (the header's brand
 * gradient), where the neutral `action.hover` / `SELECTED_OVERLAY` tints are invisible. One ramp so an
 * engaged control's fill always outranks its own hover.
 */
export const CHROME_OVERLAY_ALPHA = {
	/** Pointer-is-here tint on an otherwise ghost control. */
	HOVER: 0.1,
	/** Resting fill of an *engaged* toggle — reads as "this mode is on" on a second glance, not a shout. */
	TOGGLE_ACTIVE: 0.18,
	/** Hovering an already-engaged toggle: a step up, so hover still registers on top of the active fill. */
	TOGGLE_ACTIVE_HOVER: 0.26,
} as const;

export const LEVEL_METER = {
	/** Width of one bar. */
	BAR_WIDTH: 3,
	/** Gap between bars. */
	BAR_GAP: 2,
	/** Corner radius of a bar. */
	BAR_RADIUS: 1,
	/** Height of the shortest (lowest) bar. */
	BAR_MIN_HEIGHT: 5,
	/** Height each further bar adds, so the glyph reads as a rising ramp. */
	BAR_STEP: 3,
	/** Pill height — short enough to sit inside a `CONTROL_HEIGHT.STANDARD` field without growing it. */
	PILL_HEIGHT: 24,
	/** Max width of the level menu, so a one-line hint wraps to at most two lines. */
	MENU_MAX_WIDTH: 260,
} as const;

/**
 * Font-size scale (rem) for UIToolkit primitives sized between the theme's typography variants.
 * Prefer a matching `theme.typography` variant; reach here only for these in-between sizes.
 */
export const FONT_SIZE = {
	/** 10px — micro uppercase eyebrow on a dense instrument (hero gauge "projected" eyebrow). */
	LABEL_MICRO: "0.625rem",
	/** 10.5px — micro uppercase table-column-header labels (dense data grids). */
	LABEL_XXS: "0.65625rem",
	/** 11px — small uppercase metadata labels. */
	LABEL_XS: "0.6875rem",
	/** 11.5px — small tabular meta line (hero progress now/target labels, rescaled note). */
	LABEL_SM: "0.71875rem",
	/** 12px — inline dense-grid annotations (delta/hours) that need the caption size without its line-height/letter-spacing. */
	CAPTION: "0.75rem",
	/**
	 * 13px — secondary descriptions, hints, code-chip values.
	 *
	 * Not the general "secondary text" size: 13px and 14px were both being used for that, at three
	 * weights each and with no rule between them, which is how one card came to render its heading at
	 * 14/600 and the figures under it at 13/600. `EMPHASIS` is the secondary-text step; reach here only
	 * for a hint or a code value that has to sit a notch under it.
	 */
	SECONDARY: "0.8125rem",
	/** 14px — secondary text and emphasized labels (matches body2). Above `CAPTION`, below the title. */
	EMPHASIS: "0.875rem",
	/** 15px — empty-state headline. */
	TITLE_SM: "0.9375rem",
	/**
	 * 16px — the label on a full-width row in a touch menu or sheet. The one place body text is set
	 * above `EMPHASIS`: these rows are tap targets read at arm's length on a phone, not copy in a card.
	 */
	ROW_LABEL: "1rem",
	/**
	 * 26px — the figure on a stat tile in a KPI row. Sits between `TITLE_XXL` (a page title, which a
	 * tile's number must not compete with) and `TITLE_HERO` (a single focal identity line). A row of
	 * these is meant to be scanned, so they read as one size class rather than as several headings.
	 */
	STAT_VALUE: "1.625rem",
	/** 18px — section title (top placement). */
	TITLE_MD: "1.125rem",
	/** 19px — section title (side placement). */
	TITLE_LG: "1.1875rem",
	/** 20px — page-header title on narrow screens. */
	TITLE_XL: "1.25rem",
	/** 23px — page-header title on wide screens. */
	TITLE_XXL: "1.4375rem",
	/**
	 * 28px — the identity line of a detail page whose whole job is one entity (a candidate dossier, a
	 * record page). One step above `TITLE_XXL`, which is the working-surface header size: at that size a
	 * name reads as just another section title on a page made of cards.
	 */
	TITLE_HERO: "1.75rem",
	/** 56px — the single focal display number on an instrument hero (the current PBJ score). */
	DISPLAY_LG: "3.5rem",
} as const;

/**
 * Fluid type ramps — `clamp(min, preferred, max)` kept here so a control that has to survive a 320px
 * phone without shrinking on desktop doesn't hardcode a viewport formula inline.
 *
 * Breakpoints cannot solve this: every phone from an iPhone SE (320–375px) to a Pro Max (430px) sits in
 * MUI's `xs` bucket, so a per-breakpoint size shrinks all of them equally — or none of them. A vw-driven
 * preferred value scales continuously across that range and pins to `max` from roughly 480px up, which
 * leaves tablet and desktop exactly where they were.
 */
export const FLUID_FONT_SIZE = {
	/**
	 * 10px → 14px. Segmented-control / pill labels that must fit three-up on the narrowest phone while
	 * staying at the normal 14px body size on anything tablet-sized or larger.
	 */
	PILL_LABEL: "clamp(0.625rem, 2.9vw, 0.875rem)",
	/**
	 * 8px → 10px. A seven-up row of short figures (the gaps week strip's `−118.2` under each day): the micro label's 10px
	 * from ~385px up, stepping down only where seven columns of ~34px cannot hold a six-character value at 10px.
	 */
	SEVEN_UP_FIGURE: "clamp(0.5rem, 2.6vw, 0.625rem)",
} as const;

/**
 * Geometry for the phone bottom navigation bar (`UIToolkit/Navigation/SystemBottomNav`).
 *
 * Lives in the theme rather than inside the component because the bar's HEIGHT is load-bearing for
 * everything else on the screen: the scroller above it has to reserve exactly that much padding, and
 * every menu or sheet opened over it has to stop exactly that far from the bottom (`AdminDrawer`'s
 * `bottomOffset`). Call sites reading one number is what keeps content from hiding under the bar.
 */
/** The bar's own height, named once so `TOTAL_HEIGHT_CSS` below can be derived from it rather than restate it. */
const BOTTOM_NAV_HEIGHT_PX = 60;

export const BOTTOM_NAV = {
	/**
	 * Bar height (px), excluding the iOS home-indicator safe area the component adds underneath. Fits a
	 * `ICON_SIZE` glyph over a `LABEL_FONT_SIZE` label with breathing room, and clears `TOUCH_TARGET_MIN`
	 * on its own so a slot is a comfortable finger target without the safe-area inset having to help.
	 */
	HEIGHT: BOTTOM_NAV_HEIGHT_PX,
	/**
	 * Glyph size (px). A notch above `ICON_SIZE.CONTROL` (20px): in a bottom bar the icon IS the
	 * affordance — the label under it is a caption — so it carries more weight than a toolbar glyph.
	 */
	ICON_SIZE: 22,
	/** Slot caption. Derived from the shared micro-label step so the bar tracks the rest of the type scale. */
	LABEL_FONT_SIZE: FONT_SIZE.LABEL_XS,
	/**
	 * What the bar actually OCCUPIES: its height plus the iOS home-indicator inset it pads itself with.
	 * The CSS length the scroller above it and any sheet opened over it must reserve — a plain `HEIGHT`
	 * there leaves the last row of a list under the home indicator on every notched phone. Derived, not
	 * restated, so the two spellings cannot drift. The `0px` is CSS `env()`'s own required fallback for
	 * browsers with no safe area (a bare `env()` inside `calc()` invalidates the whole declaration).
	 */
	TOTAL_HEIGHT_CSS: `calc(${BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
} as const;

/**
 * Letter-spacing (tracking) scale for uppercase micro-labels. Em-based so the tracking scales
 * with the label's font size instead of drifting at different sizes. Reach here instead of
 * hardcoding a magic `0.5`/`1`/`0.09em` on every eyebrow / uppercase table-column header.
 */
export const LETTER_SPACING = {
	/** Standard uppercase micro-label / eyebrow tracking. */
	LABEL: "0.09em",
	/** Tightened tracking for a `FONT_SIZE.DISPLAY_LG` figure — at display size default spacing reads as loose digits. */
	DISPLAY: "-0.02em",
	/** A bold numeric or time headline set a hair tighter, so "7a-7p" reads as one figure (the sheet titles' tracking). */
	HEADLINE: "-0.01em",
	/**
	 * Tabular figures set tight: packed into a narrow column (the week strip's shortfalls, six characters in one) or a big
	 * stepper figure (`SystemStepper`), where default tracking lets two digits read as two numbers.
	 */
	TIGHT_FIGURE: "-0.02em",
} as const;

/**
 * Self-hosted font families the app ships (woff2 in `src/fonts`, registered via
 * `@font-face` in `buildAppTheme`). These are the choices the Appearance dialog
 * offers for the headline / body / label typography roles. Keep `id` stable —
 * it's what gets persisted in localStorage.
 */
export type FontId = "manrope" | "inter" | "montserrat" | "golos";

export interface FontOption {
	id: FontId;
	/** Display name (also used as a live preview of the face). */
	label: string;
	/** CSS font stack applied to `theme.typography`. */
	stack: string;
}

export const FONT_OPTIONS: readonly FontOption[] = [
	{ id: "manrope", label: "Manrope", stack: '"Manrope", sans-serif' },
	{ id: "inter", label: "Inter", stack: '"Inter", sans-serif' },
	{ id: "montserrat", label: "Montserrat", stack: '"Montserrat", sans-serif' },
	{ id: "golos", label: "Golos Text", stack: '"Golos Text", sans-serif' },
] as const;

export const FONT_STACK_BY_ID: Record<FontId, string> = Object.fromEntries(FONT_OPTIONS.map((f) => [f.id, f.stack])) as Record<FontId, string>;

// DEFAULT: app-baseline font per typography role — today's identity (Montserrat
// headlines, Golos Text body/labels). The brand fonts (Manrope / Inter) are
// applied per-user via the Appearance dialog, never as the silent global default.
export const DEFAULT_HEADLINE_FONT: FontId = "manrope";
export const DEFAULT_BODY_FONT: FontId = "inter";
export const DEFAULT_LABEL_FONT: FontId = "inter";

/**
 * Swappable app logo, for branded demos e.g. UKG. Support picks it in the Appearance
 * dialog, or a department look sets it (see theme/departmentLooks.ts).
 * `LogoId` is the stable localStorage value. Each option carries the asset for every
 * surface the logo renders on: the wide `header` wordmark, the `bedside` circular
 * icon (staff-app header), the `loading` splash image, and the `spinner` beside the
 * loading text.
 */
export type LogoId = "inhouse" | "ukg" | "ukg-plain" | "ukg-bold";

export interface LogoOption {
	id: LogoId;
	/** Display name in the Appearance picker. */
	label: string;
	/** Wide wordmark for the admin top-nav header. */
	header: string;
	/** Circular icon for the bedside (staff-app) header. */
	bedside: string;
	/** Splash/loading-screen image. */
	loading: string;
	/** The spinning icon beside the loading-screen text. UKG logos use the same icon in UKG teal. */
	spinner: string;
}

export const LOGO_OPTIONS: readonly LogoOption[] = [
	{ id: "inhouse", label: "InHouse", header: "/inHouse.svg", bedside: "/InHouseCircle.svg", loading: "/primary-logo.png", spinner: "/base-icon.png" },
	// Bedside (staff-app) keeps the InHouse circle — demos run from the admin side, so
	// there's no branded bedside mark. Only the header + loading splash swap to UKG.
	{ id: "ukg", label: "UKG", header: "/ukg.svg", bedside: "/InHouseCircle.svg", loading: "/ukg.svg", spinner: "/ukg-base-icon.png" },
	// UKG-only, no "by In-House".
	{ id: "ukg-plain", label: "UKG Plain", header: "/ukg-plain.svg", bedside: "/InHouseCircle.svg", loading: "/ukg-plain.svg", spinner: "/ukg-base-icon.png" },
	// The bold "fat" UKG lockup from Figma.
	{ id: "ukg-bold", label: "UKG Bold", header: "/ukg-bold.svg", bedside: "/InHouseCircle.svg", loading: "/ukg-bold.svg", spinner: "/ukg-base-icon.png" },
] as const;

export const LOGO_OPTION_BY_ID: Record<LogoId, LogoOption> = Object.fromEntries(LOGO_OPTIONS.map((l) => [l.id, l])) as Record<LogoId, LogoOption>;

// DEFAULT: app-baseline logo — the InHouse identity. Branded logos (UKG) are set per device,
// by support in the Appearance dialog or by a department look (the Bayview UKG demo).
export const DEFAULT_LOGO: LogoId = "inhouse";

/**
 * Census/staffing chart-series colors. Functional (one stable hue per series)
 * so the legend stays readable across user-chosen brand themes — intentionally
 * NOT theme-driven. Previously inlined in `CommandCenter`.
 */
export const CHART_SERIES = {
	CENSUS: "#8884D8",
	PPN: "#448AFF",
	/** Coral wash (30% alpha) for the out-of-bounds reference band. */
	OUT_OF_BOUNDS: "#EE5F4B4D",
} as const;

/**
 * Outline-pill segmented switch (`SystemFlatSwitch` `variant="outline"`). The border tracks the
 * user's brand color via the CSS var; the active-segment fill is a soft alpha wash over the brand
 * hex (MUI's `alpha()` rejects CSS vars — see STATUS_BADGE.BRAND_FALLBACK_HEX).
 */
export const OUTLINE_SWITCH = {
	BORDER: "var(--inhouse-primary)",
	/** Alpha applied over BRAND_FALLBACK_HEX for the active segment fill. */
	ACTIVE_FILL_ALPHA: 0.14,
} as const;

/**
 * Decorative "live data" pulse dot (chart current-day marker). Neon greens +
 * near-white stroke chosen for the glow effect — decorative, NOT brand- or
 * theme-tracked. Previously inlined as SVG fills in `ChartContainer`.
 */
export const LIVE_DOT = {
	GLOW: "#27FFAB",
	CORE: "#40EAA8",
	RING: "#F6FFFC",
} as const;

/**
 * Geometry and timing for the ambient "still checking" dot (`UIToolkit/Animations/PulseDot`): a steady dot with one
 * ring that swells out of it and fades. Slow on purpose — it sits beside copy that may stay on screen for an hour
 * (an outage screen, a sync that is still running), where a fast bounce reads as anxious rather than as "on it".
 */
export const PULSE_DOT = {
	/** Dot diameter (px): a notch under a caption's x-height, so it sits beside a line without outweighing it. */
	SIZE: 8,
	/** How far the ring swells, as a multiple of the dot, before it has faded out. */
	RING_SCALE: 2.75,
	/** Opacity the ring starts at over the dot, so the swell reads as a halo and not a second dot. */
	RING_START_OPACITY: 0.45,
	/** One swell (ms). Around a resting breath; the ring is gone well before the next one starts. */
	PERIOD_MS: 2000,
} as const;

/**
 * Icon "tile" sizes (rounded square): `size` is the px square, `glyph` the centered icon's font-size,
 * `mark` the font-size of a 2–3 character LETTERFORM in the same tile (initials / a vendor wordmark).
 *
 * `mark` is deliberately smaller than `glyph`: an icon is one centered shape, while three uppercase
 * letters at the glyph size run past the tile's padding (measured: "AoD" at 1.375rem needs ~34px of
 * the 40px SM tile, leaving no optical margin). Kept on the same token so the letterform and the icon
 * variant of a tile can never drift apart.
 */
export const ICON_TILE = {
	/** ~28px brand-tinted marker — panel headers, list-item / lever tiles. */
	XS: { size: 28, glyph: "1rem", mark: "0.625rem" },
	/** ~40px brand-tinted identity tile — section headers. */
	SM: { size: 40, glyph: "1.375rem", mark: "0.8125rem" },
	/** ~48px muted tile — empty states. */
	MD: { size: 48, glyph: "1.5rem", mark: "0.9375rem" },
	/** ~56px brand-tinted identity tile — a detail page's hero (an avatar/initials anchor beside the name). */
	LG: { size: 56, glyph: "1.75rem", mark: "1.125rem" },
} as const;

/** Geometry for the empty-state molecule (`UIToolkit/molecules/EmptyState`), per `EmptyStateSize`. */
export const EMPTY_STATE = {
	/** The text column of an in-surface empty state (a grid body, a panel) — two short lines of hint. */
	TEXT_MAX_WIDTH: 360,
	/**
	 * The text column of a full-page state (an outage screen, a page with nothing on it): two or three lines of body
	 * copy at reading size, narrow enough that a centred paragraph keeps an even rag.
	 */
	PAGE_TEXT_MAX_WIDTH: 440,
	/** Width (px) of the soft ring around a full-page state's icon tile. */
	PAGE_HALO_PX: 10,
	/**
	 * The ring's opacity over the tile's own wash: the same tone at half strength, so the tile reads as the centre of
	 * a glow rather than as a bigger square. Relative to the caller's tint, which may already be translucent.
	 */
	PAGE_HALO_OPACITY: 0.5,
} as const;

/** A sheet row's horizontal padding and the gap after its leading tile, named once so the divider inset derives from them. */
const SHEET_ROW_PADDING_X_PX = 16;
const SHEET_ROW_LEADING_GAP_PX = 12;

/**
 * Geometry for a full-width action row in a bottom sheet or touch menu (`UIToolkit/Clickable/SheetActionRow`).
 *
 * The height floor is what keeps a stack of rows even: a row with a description line and one without must read as
 * one family rather than two heights, and both clear `TOUCH_TARGET_MIN` with room to spare, because these rows are
 * aimed at with a thumb at arm's length.
 */
export const SHEET_ROW = {
	MIN_HEIGHT: 52,
	PADDING_X: SHEET_ROW_PADDING_X_PX,
	LEADING_GAP: SHEET_ROW_LEADING_GAP_PX,
	/**
	 * Where the hairline between rows starts: past the leading tile, so dividers run under the words and the tiles
	 * read as one column (an inset list). Derived from the padding, the `ICON_TILE.XS` tile and the gap after it.
	 */
	DIVIDER_INSET: SHEET_ROW_PADDING_X_PX + ICON_TILE.XS.size + SHEET_ROW_LEADING_GAP_PX,
	/**
	 * The on/off indicator a toggle row draws. Presentational only — the ROW is the switch (`role="switch"`), because a
	 * real switch input inside a row button would be interactive content nested in interactive content.
	 */
	SWITCH: { TRACK_WIDTH: 34, TRACK_HEIGHT: 20, KNOB: 16 },
} as const;

/** Width of a side drawer (`UIToolkit/Panels/SystemSideDrawer`) on a tablet or desktop; a phone gets the full width. */
export const SIDE_DRAWER = {
	/** A reading panel: a history or a detail inspector. */
	READING_WIDTH_PX: 460,
} as const;

/** Compact calendar mark in a shift history reader's viewed-shift heading. */
export const SHIFT_HISTORY_HEADER = { DATE_TILE_SIZE_PX: 52 } as const;

/** Motion for one step of a multi-step bottom sheet (`UIToolkit/Popups/SheetStep`). */
export const SHEET_STEP = {
	/**
	 * How far (px) a step's body rises from as it replaces the previous one. A nudge, not a slide: the sheet itself
	 * stays put, so the change should read as the same surface turning a page, not a new sheet arriving.
	 */
	ENTER_RISE_PX: 8,
} as const;

/**
 * How the app-wide toasts stack (`SnackbarContext`): each open toast sits one `STACK_STEP_PX` above the one before it.
 * `KEEP_ON_SCREEN` is how many a surface that lifts the toasts (a sheet over a phone's bottom bar) must leave room
 * for above the lift, so a burst of confirmations never pushes the newest off the top of the screen.
 */
export const SNACKBAR_STACK = {
	STACK_STEP_PX: 70,
	KEEP_ON_SCREEN: 3,
} as const;

/**
 * An unavailable control that must not be mistaken for a live one (`SystemButton`'s `mutedWhenDisabled`). A neutral
 * fill instead of the variant's brand or red one, a label one step above `INK.DISABLED` so it stays legible on that
 * fill, and a hairline for outlined variants. Grey on purpose: the tint is what says "you can press this".
 */
export const DISABLED_CONTROL = {
	FILL: NEUTRAL[150],
	INK: NEUTRAL[500],
	BORDER: NEUTRAL[200],
	/**
	 * A whole control or row dimmed while unavailable, rather than repainted (`SheetActionRow`, `SystemStepper`): its
	 * content stays legible and in place, it just reads as asleep. One value, so two controls on one sheet dim alike.
	 */
	DIMMED_OPACITY: 0.5,
} as const;

/**
 * Geometry and motion for a numeric stepper (`UIToolkit/Inputs/SystemStepper`) — "how many" with − and +.
 *
 * The buttons are square at `TOUCH_TARGET_MIN` on every pointer, not only coarse ones: the stepper is the control on
 * the surfaces that carry it, and a 32px − beside a finger-sized + reads as a mistake.
 */
export const STEPPER = {
	BUTTON_SIZE: TOUCH_TARGET_MIN,
	/** The value's column: wide enough for two digits at the large size, so the buttons never shift as it counts. */
	VALUE_MIN_WIDTH: { standard: 40, large: 50 },
	/** How far (px) a new value rises from as it replaces the old one — a tick, not a slide. */
	TICK_RISE_PX: 6,
} as const;

/**
 * Open Shifts on the gaps-first mobile view (`staffing/mobileGaps/openShifts/`): the request cards and their day bands.
 */
export const OPEN_SHIFTS = {
	/** The red edge an urgent claim's card carries — a notch heavier than the 2px row edges, since it rides a whole card. */
	URGENT_EDGE_PX: 3,
	/** Space between the cards of one day, and around them — the home's list rhythm (MUI spacing units). */
	CARD_GAP: 1.25,
	/**
	 * A request card's corners (× `theme.shape.borderRadius`): between a sheet's focal card (`FOCAL_CARD`, 16px) and a
	 * row chip, since a list of these sits inside the page rather than floating over it.
	 */
	CARD_RADIUS: 1.5,
} as const;

/**
 * The gaps-first mobile view's list geometry (`staffing/mobileGaps/`), shared by the home's rows and the sheets that
 * quote them.
 */
export const GAPS_LIST = {
	/** The coloured edge down the leading side of a row under a group header — and of a quoted note in a sheet. */
	EDGE_PX: 2,
} as const;

/**
 * The one focal card of a sheet step (`UIToolkit/molecules/FocalCard`): a hairline in the card's ink and a top-down wash
 * of it that fades out partway, so the card reads as THIS thing's state rather than a neutral panel.
 */
export const FOCAL_CARD = {
	/**
	 * × `theme.shape.borderRadius`: the floating-surface radius (`RADIUS.POPOVER`, 16px on the app's 8px shape) — the
	 * card is the step's one raised thing. A theme multiple, as `OPEN_SHIFTS.CARD_RADIUS` is, so it follows the shape.
	 */
	RADIUS: RADIUS.POPOVER / RADIUS.CARD,
	/** Where the wash has faded to nothing, down the card. One stop for every focal card, so no two drift apart. */
	WASH_FADE_STOP: "75%",
} as const;

/**
 * Hospital-demo palette (inhouse2__ hospital, `isHospitalView`). These hues are the
 * demo's own look from Figma — deliberately fixed, NOT brand/theme-tracked — so they
 * live here as one named source instead of hardcoded hexes scattered across the demo
 * files. Only the hospital demo reads these; every other department is unaffected.
 */
export const HOSPITAL_DEMO = {
	/** Staffing node — high load (was orange for non-demo). */
	NODE_HIGH: "#C64E49",
	/** Staffing node — low load. */
	NODE_LOW: "#00B1CE",
	/** Staffing node — medium load. */
	NODE_MEDIUM: "#9CA3AF",
	/** Green ring / role-section header (weekly gaps + detailed). */
	GREEN: "#005152",
	/** Green shift-label bar (weekly detailed day/night tabs). */
	GREEN_DARK: "#005352",
	/** Command Center actual-PPN + census line. */
	CHART_LINE: "#1F3A4D",
	/** Command Center target-range band (turquoise wash). */
	CHART_BAND: "rgba(94, 184, 249, 0.45)",
	/** Patients census-over-time line (magenta). */
	CENSUS_LINE: "#D801B5",
	/** Patients admission bar (teal). */
	CENSUS_ADMISSION: "#1F5F5B",
	/** Patients discharge bar (light teal). */
	CENSUS_DISCHARGE: "#5EBDB2",
	/** Shift-Insights feedback wash — under-staffed (blue). */
	FEEDBACK_LOW: "rgba(94, 184, 249, 0.2)",
	/** Shift-Insights feedback wash — over-staffed (red). */
	FEEDBACK_HIGH: "rgba(255, 0, 0, 0.2)",
	/** Per-area text/dot colors on the Shift-Insights breakdown, in row order. Cycles if a department has more areas. */
	BREAKDOWN_AREA_COLORS: [
		{ text: "#3E3566", dot: "#5D4E8C" },
		{ text: "#166373", dot: "#2A94AA" },
	],
} as const;

/** Font the hospital demo renders in (matches the Figma mock). Demo-only. */
export const DEMO_FONT_FAMILY = '"DM Sans", sans-serif';

/**
 * Geometry (px) for the `DayToggleGrid` day chip — the on/off week strip. Not `theme.spacing` steps:
 * the 8px scale can't express a 7px marker or a 26px chip, and the chip, its marker and the marker's
 * ring have to keep one optical relationship wherever the strip is used.
 */
export const DAY_TOGGLE = {
	/** Chip edge — small sits inside a dense table row, medium in a standalone form. */
	CELL_SM: 26,
	CELL_MD: 32,
	/** A standalone `stretch` form — one week per line, full day names, panel-width cells. */
	CELL_LG: 40,
	/** Named, not an `sx` gap: `dayToggleStripWidth` reproduces the strip's exact width from it. */
	CHIP_GAP: 3,
	CLUSTER_GAP: 12,
	/** Corner marker diameter — a status dot on a chip whose day still needs attention. */
	MARKER: 8,
	/** White ring around the marker so it stays legible on both the filled and the muted chip. */
	MARKER_RING: 1.5,
	/** Outward offset of the marker from the chip's corner (half the marker, so it straddles the edge). */
	MARKER_OFFSET: -3,
} as const;

/**
 * Geometry (px) for the Suggested Hires row — a person's whole week on one line. The accent rail is a
 * 3px vertical rule (not expressible on the 8px spacing scale) that encodes the row's state, and the
 * row height floor keeps the strip of day chips from making rows of different heights when one row's
 * controls wrap.
 */
export const SUGGESTED_HIRE = {
	/** Left state rail: default / selected / applied. */
	ACCENT_RAIL: 3,
	/**
	 * Row height floor on a fine pointer: the tallest drawn control (26px day chips / Apply) plus the
	 * row's 4px padding either side. Under 44 on purpose — on a coarse pointer the icon buttons opt
	 * into `TOUCH_TARGET_MIN` and grow the row themselves.
	 */
	ROW_MIN_HEIGHT: 36,
	/**
	 * Trailing action column. PINNED, not content-sized: an "Applied" chip is a different width from an
	 * Apply button, and an `auto` column would walk the day strips out of alignment row to row. It is
	 * the `minmax(…, 1fr)` minimum, sized for the coarse-pointer case where the icon buttons are 44px.
	 */
	ACTIONS_WIDTH: 156,
	/**
	 * Identity column — name plus its pill run, one line.
	 *
	 * FIXED, not a min: the day strip starts in the next column, so anything content-sized here makes
	 * the strips start at a different x on every row. Budgeted from measured text advances for the
	 * widest realistic pill strings, across all four selectable label fonts, plus slack. Assumes a
	 * 2-week cycle (`3-3 PER WK`); a longer cycle needs the budget re-derived.
	 */
	IDENTITY_WIDTH: 478,
	/**
	 * Name width inside the identity column, `border-box` — 14px of it is the input's own padding and
	 * border. Sized to the worst generated name ("4pm-12am Part Time 2"), whose numeric suffix is what
	 * tells two identical rows apart and so may never be clipped. A longer user-typed name ellipsizes.
	 */
	NAME_WIDTH: 172,
} as const;

/**
 * **Fixed interactive accent** — the blue an accent-specified surface draws its actions in.
 *
 * Not `theme.palette.primary` (customer-configurable, so a design-specified blue would drift the
 * moment an org picks a brand colour) and not `STATUS.INFO` (which means "informational"). The
 * trade-off is explicit: nothing drawn from here re-tints with the brand, so prefer
 * `theme.palette.primary` unless a design specifically calls for this accent.
 */
export const ACCENT_BLUE = {
	/** Solid fill for a primary action, and the fill of an "on" toggle. */
	MAIN: "#2563EB",
	/** Hover/pressed step down from `MAIN`. */
	DARK: "#1D4ED8",
	/** Readable ink for accent text on a white or lightly-tinted surface. */
	INK: "#1E40AF",
	/** Text/glyph colour on top of `MAIN`. */
	ON_ACCENT: SURFACE,
	/** Light wash behind a selected row or a hovered "off" toggle. */
	TINT: "rgba(37, 99, 235, 0.10)",
	/** Hairline border of a selected row / accent-outlined control. */
	BORDER: "rgba(37, 99, 235, 0.32)",
} as const;

/**
 * **Committing-action navy** — the one button that commits a dialog's whole decision.
 *
 * Not `ACCENT_BLUE`: that marks the many small actions on a working surface, this is the single
 * terminal step at the foot of a modal. Fixed across themes — it belongs to an approved design, not to
 * the configurable brand.
 */
export const ACTION_NAVY = {
	/** Solid fill of a committing footer action. */
	MAIN: "#1E293B",
	/** Hover/pressed step down from `MAIN`. */
	DARK: "#0F172A",
	/** Text/glyph colour on top of `MAIN`. */
	ON_ACTION: SURFACE,
} as const;

/**
 * Hiring Module gap fills — the tints behind its two shortfall tiers, in the gaps cell and the legend
 * chip over it. Its OWN fills, not `COVERAGE.*`: that scale is shared with the master-schedule v3
 * grids where `WARNING_BG` also means SURPLUS, so re-tinting it for a hiring legend restyled them.
 * Ink, bar and marker stay shared. Re-measure contrast against `COVERAGE.*_INK` before changing these.
 */
export const GAP_TONE = {
	CRITICAL_BG: "#FEF1F0",
	WARNING_BG: "#FEF8E7",
} as const;

/** Daily Gaps marks a staff request to replace agency coverage with a light overlay on its posted fill. */
export const DAY_GAPS_POSTED = {
	LOW_PRIORITY_HATCH: `repeating-linear-gradient(135deg, ${alpha(SURFACE, 0.55)} 0 4px, transparent 4px 8px)`,
	LOW_PRIORITY_INK: INK.SECONDARY,
	LOW_PRIORITY_WEIGHT: 400,
} as const;

/**
 * Geometry (px) for a **person row** (`UIToolkit/Cells/PersonActionRow`). The height floors are the
 * point: without them a stack goes ragged the moment one row has no meta line or its action wraps.
 */
export const PERSON_ROW = {
	MIN_HEIGHT: 56,
	/** Dense variant — inside a card that already has its own header. */
	MIN_HEIGHT_DENSE: 46,
} as const;

/**
 * Geometry (px) shared by the bare selection glyphs — `RoundedCheckboxBox` and its radio sibling
 * `RoundedRadioBox` — so a checkbox column and a radio column line up to the pixel.
 */
export const SELECTION_GLYPH = {
	/** Edge of the box / ring. */
	SIZE: 18,
	/** Edge line weight. */
	BORDER_WIDTH: 1.5,
	/** The radio's inner dot. */
	RADIO_DOT_SIZE: 8,
	/** The mark (check / dot) settles in from this scale, never from 0 (Application Language motion rule). */
	MARK_ENTER_SCALE: 0.6,
} as const;

/**
 * Geometry (px) for a **value-choice tile** (`UIToolkit/Clickable/ValueChoiceGrid`). A height floor
 * rather than padding: the captions vary ("30th", "median"), and without it the values in a grid stop
 * sharing a baseline.
 */
export const VALUE_CHOICE = {
	TILE_MIN_HEIGHT: 62,
	/** Line height of a tile's compact text lines (the eyebrow, the outline figure and caption). */
	COMPACT_LINE_HEIGHT: 1.3,
	/** A secondary line on a SELECTED solid tile inherits the fill's ink, eased back a notch under the figure. */
	SELECTED_SECONDARY_OPACITY: 0.85,
	/**
	 * The `tileVariant="outline"` card (a shift window, a plan tier): white tile, hairline border, and a
	 * selected state carried by a brand ring + wash + check rather than a solid fill — so a row of wide,
	 * left-aligned cards can show one choice without the fill shouting over the figures beside it.
	 */
	OUTLINE: {
		/**
		 * Extra inset ring (px) on the selected card. With the 1px border it reads as a 2px brand edge, and
		 * because it is a box-shadow the content does not move by a pixel when selection changes (a real
		 * 2px border would nudge the figure 1px down-right on every pick).
		 */
		SELECTED_RING: 1,
		/** Edge (px) of the brand check-circle in the selected card's top-right corner. */
		CHECK_SIZE: 18,
		/**
		 * Resting scale of the check before it lands — it grows to 1 as the card is picked. Well above 0 on
		 * purpose (Application Language: never animate from `scale(0)`), so it reads as a settle, not a pop.
		 */
		CHECK_ENTER_SCALE: 0.6,
		/**
		 * Soft brand lift under the selected card. A builder rather than a string because the ink is the
		 * caller's accent (`theme.palette.primary.main` by default), so the lift re-tints with the org.
		 */
		selectedShadow: (accent: string) => `0 2px 8px ${alpha(accent, ALPHA.SHADOW_MEDIUM)}`,
		/**
		 * Inset (px) of a tile's secondary action (a "Clear" on a Custom card holding a value) from the card's
		 * top-right corner. With the micro control height it centres on the eyebrow line, in the slot the check
		 * otherwise occupies, so the action reads as part of the card rather than floating over it.
		 */
		ACTION_INSET: 10,
		/** Height (px) of that action — the micro control size: a quiet in-content link, not a second button row. */
		ACTION_HEIGHT: CONTROL_HEIGHT_PX.MICRO,
		/** Horizontal padding (px) inside the action, so its hover wash has room around the glyph and label. */
		ACTION_PADDING_X: 6,
		/** The action lands from this scale as the card takes a value (never from 0 — motion rule). */
		ACTION_ENTER_SCALE: 0.95,
	},
} as const;

// Shared stops of the Annie avatar palette below — named once because several parts reuse them.
const ASSISTANT_AVATAR_INK_ON_LIGHT = "#1F1D33";
const ASSISTANT_AVATAR_INK_ON_DARK = "#F4F3FF";
const ASSISTANT_AVATAR_CHEEK = "#FF8FA3";
const ASSISTANT_AVATAR_LAVENDER_LIGHT = "#C4C1E6";
const ASSISTANT_AVATAR_WARM_GOLD = "#FFC94D";

/**
 * Annie's avatar (`UIToolkit/AssistantAvatar`) — the placeholder part palette until final artwork lands.
 * Fixed, not tenant-themed: a user picks these parts by name, so they must look the same in every org.
 * The one exception is the `theme` body color, which the component resolves from
 * `theme.palette.primary` at runtime, so it has no entry here. `BODY`/`SKIN`/`HAIR` keys mirror the
 * `AssistantAvatarColor` / `AssistantAvatarSkinTone` / `AssistantAvatarHairColor` enum values.
 * Re-check feature-ink contrast on MIDNIGHT and on TONE_6 before changing these.
 */
export const ASSISTANT_AVATAR = {
	/** Rendered size (px) on each personal Annie surface. */
	SIZE: {
		LAUNCHER: 48,
		HEADER: 28,
		TURN: 28,
		GREETING: 56,
		MENU: 24,
		NUDGE: 28,
		/** The avatar that walks a guided tour. The smallest size where the pointing hand still reads. */
		TOUR_GUIDE: 56,
	},
	/** Character body / human scrubs + backdrop. `LIGHT` → `MAIN` is the top → bottom gradient. */
	BODY: {
		LAVENDER: { LIGHT: ASSISTANT_AVATAR_LAVENDER_LIGHT, MAIN: "#7B77AA", FEATURE_INK: ASSISTANT_AVATAR_INK_ON_LIGHT },
		GOLD: { LIGHT: "#FFE9A3", MAIN: "#F2B84B", FEATURE_INK: ASSISTANT_AVATAR_INK_ON_LIGHT },
		MINT: { LIGHT: "#B4ECD9", MAIN: "#3FB38F", FEATURE_INK: ASSISTANT_AVATAR_INK_ON_LIGHT },
		CORAL: { LIGHT: "#FFC9BF", MAIN: "#EE7766", FEATURE_INK: ASSISTANT_AVATAR_INK_ON_LIGHT },
		SKY: { LIGHT: "#C9E4FF", MAIN: "#5A9EE8", FEATURE_INK: ASSISTANT_AVATAR_INK_ON_LIGHT },
		MIDNIGHT: { LIGHT: "#6F6BAA", MAIN: "#2E2B55", FEATURE_INK: ASSISTANT_AVATAR_INK_ON_DARK },
	},
	/** Eyes/mouth ink. The `theme` body picks ON_LIGHT or ON_DARK by contrast against the org's primary. */
	FEATURE_INK: {
		ON_LIGHT: ASSISTANT_AVATAR_INK_ON_LIGHT,
		ON_DARK: ASSISTANT_AVATAR_INK_ON_DARK,
		HUMAN: "#241616",
	},
	SKIN: {
		TONE_1: "#FCE3D2",
		TONE_2: "#F2C9A5",
		TONE_3: "#DDA877",
		TONE_4: "#B97F52",
		TONE_5: "#8D5A3B",
		TONE_6: "#5E3A26",
	},
	HAIR: {
		BLACK: "#2B2323",
		BROWN: "#5A3A2A",
		CHESTNUT: "#8A5530",
		AUBURN: "#A5462A",
		BLONDE: "#E2B864",
		SILVER: "#BDBAC6",
		PINK: "#E88DB8",
		BLUE: "#5E7FE0",
	},
	FACE: {
		SCLERA: SURFACE,
		GLINT: alpha(SURFACE, 0.9),
		/** Top-left highlight on a character body. */
		SHINE: alpha(SURFACE, 0.35),
		CHEEK_CHARACTER: alpha(ASSISTANT_AVATAR_CHEEK, 0.45),
		CHEEK_HUMAN: alpha(ASSISTANT_AVATAR_CHEEK, 0.35),
		TONGUE: ASSISTANT_AVATAR_CHEEK,
		/** Shadow under the chin on the neck. */
		SKIN_SHADE: alpha(INK_BLACK, 0.08),
		/** Collar fold line. */
		FOLD_LINE: alpha(INK_BLACK, 0.12),
		HAND_OUTLINE_CHARACTER: alpha(INK_BLACK, 0.06),
		HAND_OUTLINE_HUMAN: alpha(INK_BLACK, 0.08),
	},
	ACCESSORY: {
		/** Outline ink shared by glasses, headphones, stethoscope and the coffee cup. */
		INK: "#3B3A52",
		CAP: "#4FB3A9",
		CAP_BRIM: "#3A8F86",
		CAP_DOT: alpha(SURFACE, 0.7),
		LENS: alpha(SURFACE, 0.18),
		SHADES: ASSISTANT_AVATAR_INK_ON_LIGHT,
		SHADES_GLINT: alpha(SURFACE, 0.6),
		CHEST_PIECE: "#D9D8E6",
		HEADPHONE_CUP: "#F2877A",
		COFFEE_CUP: SURFACE,
		COFFEE: "#C98B5A",
		STEAM: "#9A99AD",
		FLOWER_PETAL: "#FFD1DC",
		FLOWER_CENTER: ASSISTANT_AVATAR_WARM_GOLD,
		HEARING_AID: "#7FB6E8",
	},
	/** Mood extras drawn around the figure. */
	EXTRAS: {
		THINKING_DOT: "#B8B6CF",
		SPARKLE_GOLD: ASSISTANT_AVATAR_WARM_GOLD,
		SPARKLE_MINT: "#8FD9C0",
		SPARKLE_LAVENDER: ASSISTANT_AVATAR_LAVENDER_LIGHT,
		GROUND_SHADOW: alpha("#1A1A2E", 0.09),
	},
	/**
	 * The "this is AI" disc on every human avatar. Its fill and glyph are `theme.palette.primary.main` /
	 * `.contrastText` (the Annie brand mark's colors, readable on any org primary), so only the ring is
	 * fixed. `MIN_DIAMETER_PX` keeps it visible at the 28px chat size, where the drawn size alone would
	 * be a 4px speck.
	 */
	AI_MARKER: {
		RING: SURFACE,
		MIN_DIAMETER_PX: 8,
	},
	/** Count dot. Sized in screen px, not art units, so it reads the same on a 56px launcher and a 210px stage. */
	BADGE: {
		FILL: STATUS.ERROR.main,
		RING: SURFACE,
		TEXT: SURFACE,
		DIAMETER_PX: BADGE.PILL_SIZE,
		RING_PX: 2,
		FONT_PX: 11,
		/** Counts above this render as `${MAX_COUNT}+` so the text never outgrows the dot. */
		MAX_COUNT: 9,
	},
	/** Opacity applied on top of a user-picked hair color. */
	OPACITY: {
		STUBBLE: 0.22,
		BUZZ: 0.9,
	},
	/**
	 * Mood motion (ms). Loops only where they show real work (thinking, talking) or the idle blink;
	 * needs-you and done are one-shot. Longer than the UI budget on purpose: these are character
	 * beats, not chrome transitions.
	 */
	MOTION: {
		BLINK: 6500,
		PONDER: 3000,
		THINKING_DOT: 1200,
		THINKING_DOT_STAGGER: 200,
		TALK: 320,
		HOP: 900,
		WAVE: 1200,
		JUMP: 900,
		SPARKLE: 1200,
		SPARKLE_STAGGER: 100,
		/** One push of the pointing hand toward its target; it plays twice, then rests. */
		POINT_NUDGE: 900,
		/** Pupils gliding to a new `gaze` target. */
		GAZE: 280,
		/** A tour guide's arrival hop on each step. */
		TOUR_TRAVEL: 600,
		/** How long a tour guide talks per character of its text, clamped to `TOUR_TALK_MIN`…`TOUR_TALK_MAX`. */
		TOUR_TALK_PER_CHAR: 18,
		TOUR_TALK_MIN: 600,
		TOUR_TALK_MAX: 1800,
		/** A tour guide starts talking this long after a step opens, as she settles from the move. */
		TOUR_TALK_DELAY: 300,
		/** How long a tour guide holds the oops tilt after a click on the wrong thing. */
		TOUR_OOPS: 1200,
		/** Gentle ease-out for the one-shot hop and jump. */
		EASE_ONE_SHOT: "cubic-bezier(0.3, 0.7, 0.4, 1)",
		/** On-screen back-and-forth (tilt, dots, mouth, wave). */
		EASE_LOOP: "ease-in-out",
	},
} as const;

/** `UIToolkit/Walkthrough/WalkthroughGuideSlot` — the tour guide in the step card's header slot. */
export const WALKTHROUGH_GUIDE = {
	/** Height of the little hop as the guide arrives on each step (px). */
	HOP_PX: 14,
} as const;

/** `UIToolkit/Clickable/VisualChoiceGrid` geometry (px). */
export const VISUAL_CHOICE = {
	TILE: { SM: 48, MD: 56 },
	SWATCH: 34,
	SELECTED_RING_PX: 3,
	SELECTED_RING_ALPHA: 0.18,
	SWATCH_RING_PX: { REST: 1.5, SELECTED: 2.5 },
	SWATCH_GAP_PX: 3,
	TILE_BORDER_PX: 1.5,
	HOVER_BORDER_ALPHA: 0.5,
	HOVER_LIFT_PX: 2,
	PRESS_SCALE: 0.97,
	DISABLED_OPACITY: 0.5,
} as const;

/** Annie Studio (`components/Annie/AnnieStudio`). The stage wash is `alpha(theme.palette.primary.main, WASH.*)`. */
export const ANNIE_STUDIO = {
	STAGE_WIDTH: 300,
	STAGE_AVATAR: 212,
	STAGE_AVATAR_COMPACT: 84,
	PRESET_AVATAR: 40,
	PART_AVATAR: 46,
	/** Larger than the tile, cropped to the head. */
	FACE_AVATAR: 80,
	BODY_HEIGHT: "min(600px, calc(100dvh - 260px))",
	WASH: { CORE: 0.24, EDGE: 0.04 },
	HIDDEN_OPACITY: 0.35,
	HIDDEN_GRAYSCALE: 0.8,
	POP_SCALE: { FROM: 0.92, OVERSHOOT: 1.04 },
	BUBBLE_RISE_PX: 4,
	BUBBLE_RADIUS_PX: 16,
	/** The corner nearest her (inline-start, bottom), so it follows reading direction. */
	BUBBLE_TAIL_RADIUS_PX: 4,
	STAGE_BORDER_ALPHA: 0.12,
} as const;

/**
 * Geometry (px) for a **named group card** (`UIToolkit/molecules/NamedGroupCard`) — a titled, toned
 * group of people/things with the names behind it.
 */
export const NAMED_GROUP = {
	/** The tone's left accent rail — the same weight as the active stat tile's, so the two read as one family. */
	ACCENT_RAIL_WIDTH: 3,
	/** Revealed names scroll past this — a notch under the default, since several groups stack in one dialog. */
	LIST_MAX_HEIGHT: 176,
	/** At or below this many names a group starts revealed — naming three people is the useful case. */
	AUTO_EXPAND_MAX: 8,
} as const;

/** Geometry (px) for a revealable list of names (`UIToolkit/molecules/NameDisclosureList`). */
export const NAME_DISCLOSURE = {
	/** Revealed names scroll past this, so a list of ~840 people can't push the rest of a dialog away. */
	LIST_MAX_HEIGHT: 220,
	/** Column floor for the `columns` layout: two-up in a narrow dialog, three- or four-up in a wide one. */
	COLUMN_MIN_WIDTH: 160,
} as const;

/**
 * The focal band of a before → after summary (e.g. the bulk job-change review): a diagonal brand wash
 * under a large count. Alphas are applied to `theme.palette.primary.main` at the call site so the band
 * re-tints with the org's accent.
 */
export const CHANGE_HERO = {
	/** Wash at the band's leading corner, fading to transparent — deliberately a notch over `ALPHA.HERO_WASH`, since the band carries only a number and a line. */
	WASH_ALPHA: 0.14,
	/** Where the wash has faded out, as a share of the band's diagonal. */
	WASH_STOP: "80%",
	/** Hairline border that separates the band from the white dialog surface. */
	BORDER_ALPHA: 0.18,
	/** Soft brand halo (px) around the band's "after" item — the thing the change lands on — so it reads as the destination. */
	AFTER_HALO_PX: 4,
	/** How far the "after" item slides in from, so the change reads as arriving rather than appearing. */
	ARRIVE_OFFSET_PX: 6,
	/** The scale the kept item lands from — a small settle, not a zoom. */
	LAND_FROM_SCALE: 0.96,
} as const;

/**
 * Geometry and washes for a side-by-side record comparison (`UIToolkit/Compare/SideBySideCompare`).
 *
 * The chosen side is drawn as one continuous LANE — its header card and every value cell under it share a
 * brand wash — so "which one am I keeping" reads down the whole column, not just at the top. Alphas are
 * applied to `theme.palette.primary.main` at the call site so the lane re-tints with the org's accent.
 */
export const SIDE_BY_SIDE_COMPARE = {
	/** Width of the field-name column from `sm` up; below it the label moves above its values. */
	LABEL_COLUMN_WIDTH: 152,
	/** Floor for a value row, so a row of one-word values and one of two-line values still read as one table. */
	ROW_MIN_HEIGHT: 40,
	/** The chosen side's lane under its header — quieter than the header's wash, so the values on it keep their full ink. */
	LANE_WASH_ALPHA: 0.05,
	/** The chosen side's header card: one step above the lane, so the card still reads as the thing you clicked. */
	HEADER_WASH_ALPHA: ALPHA.SELECTED_OVERLAY,
	/** The chosen header's ring — heavier than a hairline so the choice survives a glance. */
	SELECTED_RING_WIDTH: 1.5,
	/**
	 * A lane's outer corners, in px — `RADIUS.CONTROL`, the bordered-control-card radius. Pixels, not a theme
	 * multiple, because a lane rounds only two corners and MUI multiplies only the `borderRadius` shorthand.
	 */
	LANE_RADIUS_PX: RADIUS.CONTROL,
} as const;

/**
 * The staff picker's controlled selection modes (`NursesMatrixGrid` with `selectionMode`): a leading
 * radio/checkbox column, a soft brand wash on picked rows, the struck "can't work this shift" rows and
 * the dashed "+" that previews the pending shift on the target day. Alphas are applied to
 * `theme.palette.primary.main` at the call site so every wash re-tints with the org's accent.
 */
export const STAFF_PICKER = {
	/**
	 * Height of the Add Staff panel: it fills the Staffing Changes popup below its tab strip, and the grid inside
	 * takes whatever the header, filters and footer leave. The Remove Staff panel is content-sized instead and
	 * only takes this as its ceiling (see `REMOVE_GRID_MAX_HEIGHT`).
	 */
	PANEL_HEIGHT: "calc(90vh - 48px)",
	/**
	 * Remove Staff's grid (its body, below the pills + search row) sizes to its rows up to this, then scrolls,
	 * with the footer right under it. Sized so the Staffing Changes popup keeps the old Remove Nurse popup's
	 * footprint: 770px tall in a 900px window. Worked from the stacked footer this replaced — at 900px that popup
	 * was 811px with the cap at 80vh − 300 (420px); folding the footer into one row (1 + 16 +
	 * `CONTROL_HEIGHT.LARGE` + 20 = 85px, down from ≈ 172) frees 87px, and 46 of them go back to rows:
	 * 811 − 87 + 46 = 770. A picked reason's note row adds ≈ 52px on demand. The recommendation's popup (no tab
	 * strip) sits ≈ 40px under its `APPROVAL_DIALOG.MAX_HEIGHT` cap, which only the note row can reach.
	 */
	REMOVE_GRID_MAX_HEIGHT: "calc(80vh - 254px)",
	/**
	 * The footer's "Removing …" read-back takes the action row's spare room, down to this (px) before the buttons
	 * wrap under it — so at desktop widths legend, reason, read-back and buttons share one row, and a long
	 * read-back ellipsizes rather than wrapping.
	 */
	READ_BACK_MIN_WIDTH: 160,
	/**
	 * The step-2 search box flexes between these widths (px): it takes the toolbar's spare room up to the max,
	 * and gives it back down to the min before the toolbar wraps below the step heading.
	 */
	SEARCH_MIN_WIDTH: 240,
	SEARCH_MAX_WIDTH: 360,
	/**
	 * Step 1's Job / Location fields share a row under the shift cards, each between these widths (px): wide
	 * enough for a long job or location name, narrow enough that two sit side by side on a tablet.
	 */
	SCOPE_FIELD_MIN_WIDTH: 240,
	SCOPE_FIELD_MAX_WIDTH: 300,
	/** Radius (× `theme.shape.borderRadius`) of the dashed "Pick a shift first" well that stands in for the grid. */
	LOCKED_WELL_RADIUS: 1.5,
	/** Gap (px) between the footer's "3 staff" and its dotted underline — clear of descenders, still attached. */
	SUMMARY_UNDERLINE_OFFSET: 3,
	/** Radius (× `theme.shape.borderRadius`) of that label's focus ring: the badge radius, so it hugs the text. */
	SUMMARY_FOCUS_RADIUS: GRID_RADIUS.BADGE,
	/** Wash under a picked row — over white it lands on the design's pale lilac (≈ #F6F5FB at the default brand). */
	SELECTED_ROW_WASH_ALPHA: 0.07,
	/** The same row under the pointer: one notch deeper, so hover still reads on an already-picked row. */
	SELECTED_ROW_HOVER_WASH_ALPHA: 0.11,
	/** Brand rail (px) on the leading edge of the keyboard cursor row — a position marker, not a second selection. */
	CURSOR_RAIL_WIDTH: 3,
	/** Width (px) of the leading radio/checkbox column. */
	SELECT_COLUMN_WIDTH: 52,
	/** A blocked row's role pill + struck name recede to this — present, but clearly not an option. */
	BLOCKED_NAME_OPACITY: 0.55,
	/**
	 * Line height of the name + `nameCaption` stack (a nurse's custom hours under their name). Tight enough
	 * that the two lines (~33px) sit inside a `GRID_ROW_HEIGHT.STANDARD` row with air above and below, so a
	 * captioned row and a plain one stay the same height.
	 */
	NAME_CAPTION_LINE_HEIGHT: 1.25,
	/** A blocked row's week strip (`ALPHA.STRUCK`): its shifts stay legible as context for the reason. */
	BLOCKED_STRIP_OPACITY: ALPHA.STRUCK,
	/** Diameter (px) of the dashed "+" pending-shift ring on an empty target day. */
	PENDING_MARKER_SIZE: 26,
	/** Diameter (px) of the corner "+" when the target day already carries a shift icon it must not cover. */
	PENDING_MARKER_BADGE_SIZE: 14,
	/** The "+" glyph (px) inside the full ring / inside the corner badge. */
	PENDING_GLYPH_SIZE: 16,
	PENDING_GLYPH_SIZE_COMPACT: 10,
	/** Inset (px) of the corner badge from the top of the day cell. */
	PENDING_BADGE_OFFSET: 2,
	/**
	 * Horizontal shift (px) of the corner badge's left edge from the cell centre — it sits as a superscript
	 * on the 16px shift icon rather than on top of it (the cell's content box is only ~26px wide).
	 */
	PENDING_BADGE_SHIFT_X: 4,
	/** Dash weight (px) of the pending ring. */
	PENDING_MARKER_BORDER: 1.5,
	/** The pending ring lands from this scale (never from 0 — Application Language motion rule). */
	PENDING_ENTER_SCALE: 0.85,
} as const;

/**
 * A numbered step heading in a multi-step panel (`UIToolkit/molecules/StepHeader`): "1 Which shift?",
 * "2 Who can work it?". The badge is a filled brand circle once the step is reachable and a muted
 * neutral one before it, so the panel reads as a sequence at a glance.
 */
export const STEP_HEADER = {
	/** Edge (px) of the numbered circle — the micro control size, so it centres on a 16px title line. */
	BADGE_SIZE: CONTROL_HEIGHT_PX.MICRO,
} as const;

/**
 * Geometry and washes for a suggested duplicate pair on the Team page (`components/team/DuplicateEmployees`).
 *
 * Each row is two records joined by an evidence BRIDGE: a brand hairline running from one record to the other,
 * with the reasons sitting on it in a small raised node. The value a reason is about (the shared phone, email or
 * name) is washed on BOTH records, so the match is read in the data rather than only in a chip. Alphas are applied
 * to `theme.palette.primary.main` at the call site so the bridge and the washes re-tint with the org's accent.
 */
export const DUPLICATE_PAIR = {
	/** The search above the list: wide enough for a full name, narrow enough to read as a filter, not a form. */
	SEARCH_MAX_WIDTH: 360,
	/** Three lines per record — name, facilities, contact — with room above and below; also fits the two stacked actions. */
	ROW_HEIGHT: 104,
	/** Floor for each record column before the grid scrolls sideways — all four columns plus the grid's scrollbar fit a 900px tablet. */
	RECORD_MIN_WIDTH: 228,
	/** Below this record width the login reads as its key glyph alone, so the email keeps room to be read. */
	RECORD_COMPACT_WIDTH: 340,
	/** The least an email keeps before the rest of its line — a few characters plus the ellipsis, never an empty wash. */
	CONTACT_MIN_WIDTH: 72,
	/** The evidence column between the records: two reason chips side by side, plus the line's stubs either side. */
	BRIDGE_COLUMN_WIDTH: 212,
	/** How much of the line stays visible on each side of the node — the part that reads as "these two are linked". */
	BRIDGE_STUB_PX: 22,
	ACTIONS_COLUMN_WIDTH: 176,
	/** The bridge's hairline at full strength; it fades to nothing at both record edges. */
	BRIDGE_LINE_ALPHA: 0.3,
	BRIDGE_LINE_WIDTH_PX: 1.5,
	/** Where the hairline reaches full strength, from each edge of the column. */
	BRIDGE_LINE_FADE_STOP: "10px",
	/** The raised node that carries the reason chips — a hairline edge one step under the line itself. */
	BRIDGE_NODE_BORDER_ALPHA: 0.2,
	/** Wash behind a value that is the same on both records — a notch over the brand tint so it survives on a hovered row. */
	MATCH_WASH_ALPHA: 0.14,
} as const;

/**
 * Onboarding roster upload: the grid of rows whose person already has another staff record. It sits inside the Data
 * Load result card, ~130px narrower than the Team tab, so its floors are tighter than `DUPLICATE_PAIR`'s: all four
 * columns plus the scrollbar fit the card on an 834px tablet, keeping "Review & merge" on screen. Row height and
 * the action column reuse `DUPLICATE_PAIR`.
 */
export const ROSTER_MERGE_GRID = {
	/** Each record column's floor — the record cell drops to its compact form (login as a glyph) well above this. */
	RECORD_MIN_WIDTH: 190,
	/** A roster match carries at most three reasons, which the bridge stacks in one column: one chip plus the stubs. */
	BRIDGE_COLUMN_WIDTH: 132,
} as const;
